import React, { useState, useEffect, createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../context/auth/AuthContext';

// Comprehensive mapping from Retell.ai product names to user-friendly labels
const PRODUCT_LABEL_MAPPING = {
  // Voice Engine variations
  'elevenlabs_tts': 'Voice Engine',
  'elevenlabs': 'Voice Engine',
  'cartesia': 'Voice Engine',
  'tts': 'Voice Engine',
  'voice_engine': 'Voice Engine',
  'Voice Engine': 'Voice Engine',
  
  // LLM variations
  'gpt_4o': 'LLM',
  'gpt_4_1': 'LLM',
  'gpt4o': 'LLM',
  'gpt4o_mini': 'LLM',
  'gpt-4o': 'LLM',
  'gpt-4o-mini': 'LLM',
  'claude': 'LLM',
  'llm': 'LLM',
  'LLM': 'LLM',
  
  // Knowledge Base variations
  'knowledge_base_usage': 'Knowledge Base Usage',
  'knowledge_base': 'Knowledge Base Usage',
  'kb_usage': 'Knowledge Base Usage',
  'kb': 'Knowledge Base Usage',
  'Knowledge Base Usage': 'Knowledge Base Usage',
  
  // Phone Number variations
  'phone_number': 'Phone Number',
  'phone_numbers': 'Phone Number',
  'Phone Number': 'Phone Number',
  
  // Text Testing variations
  'text_testing': 'Text Testing',
  'Text Testing': 'Text Testing',
  
  // Surcharge variations
  'short_call_surcharge': 'Short Call Surcharge',
  'Short Call Surcharge': 'Short Call Surcharge',
  'llm_token_surcharge': 'LLM Token Surcharge',
  'LLM Token Surcharge': 'LLM Token Surcharge',
  
  // Features variations
  'features': 'Features',
  'Features': 'Features',
  
  // Concurrency variations
  'concurrency': 'Concurrency',
  'Concurrency': 'Concurrency',
  
  // General variations
  'general': 'General',
  'General': 'General'
};

// Function to get user-friendly label from product name
const getProductLabel = (productName) => {
  if (!productName) return 'Unknown';
  
  // Try exact match first
  if (PRODUCT_LABEL_MAPPING[productName]) {
    return PRODUCT_LABEL_MAPPING[productName];
  }
  
  // Try case-insensitive match
  const lowerProductName = productName.toLowerCase();
  for (const [key, value] of Object.entries(PRODUCT_LABEL_MAPPING)) {
    if (key.toLowerCase() === lowerProductName) {
      return value;
    }
  }
  
  // Try partial match for complex product names
  for (const [key, value] of Object.entries(PRODUCT_LABEL_MAPPING)) {
    if (lowerProductName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerProductName)) {
      return value;
    }
  }
  
  // Fallback: return the original name with proper capitalization
  return productName.charAt(0).toUpperCase() + productName.slice(1).toLowerCase();
};

export const BillingContext = createContext({ totalAmount: 0, setTotalAmount: () => {} });

const Billing = ({ totalAmount, setTotalAmount }) => {
  const { userName } = useAuth();
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set(['current-usage', '2025-06']));
  const [callDetails, setCallDetails] = useState([]);
  const [stripeInvoices, setStripeInvoices] = useState([]);
  const [billingStatusByMonth, setBillingStatusByMonth] = useState([]);
  const [phoneNumberBilling, setPhoneNumberBilling] = useState([]);
  const [monthlyTotals, setMonthlyTotals] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Store API response data for proper month association
  const [apiBillingData, setApiBillingData] = useState(null);
  
  // Store Component C (Phone Numbers) data from API
  const [apiPhoneNumbersData, setApiPhoneNumbersData] = useState(null);
  
  // Store Component B (Chat Sessions) data from API for Text Testing
  const [apiChatSessionsData, setApiChatSessionsData] = useState(null);
  const [exactComponentBAmount, setExactComponentBAmount] = useState(null);
  
  // Store Component A (Voice Calls) data from API when available
  const [apiVoiceCallsData, setApiVoiceCallsData] = useState(null);
  
  // Store billing data per month (Component A, B, C for each billing period)
  const [monthlyBillingData, setMonthlyBillingData] = useState({}); // { "2025-10": { componentA: {...}, componentB: {...}, componentC: {...} } }
  
  
  // Data preservation for sync operations
  const [preSyncCallDetails, setPreSyncCallDetails] = useState(null);
  const [preSyncBillingStatus, setPreSyncBillingStatus] = useState(null);
  const [preSyncPhoneNumberBilling, setPreSyncPhoneNumberBilling] = useState(null);
  
  // Force refresh trigger for manual updates
  const [forceRefresh, setForceRefresh] = useState(0);
  
  // Enhanced sync feedback states
  const [syncMessage, setSyncMessage] = useState('');
  const [syncMessageType, setSyncMessageType] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Feature flag: disable periodic auto-refresh (user requested no auto refresh)
  const AUTO_REFRESH_ENABLED = false;
  // Feature flag: preserve existing data while refreshing to avoid flicker
  const CLEAR_ON_REFRESH = false;
  // Master flag: disable any post-initial refreshes globally
  const REFRESH_ENABLED = false;
  // Prevent React 18 StrictMode double-effect re-run during development
  const didInitRef = useRef(false);
  

  
  
  // Enhanced error handling and graceful degradation
  const ErrorHandler = {
    // Error states
    states: {
      LOADING: 'loading',
      SUCCESS: 'success',
      ERROR: 'error',
      FALLBACK: 'fallback',
      RETRY: 'retry'
    },
    
    // Current error state
    currentState: 'success',
    
    // Error messages
    messages: {
      NETWORK_ERROR: 'Network connection failed. Using cached data.',
      API_ERROR: 'API request failed. Using fallback data.',
      PARSE_ERROR: 'Data parsing failed. Using static data.',
      VALIDATION_ERROR: 'Data validation failed. Using backup data.',
      TIMEOUT_ERROR: 'Request timed out. Retrying...',
      AUTH_ERROR: 'Authentication failed. Please log in again.',
      PERMISSION_ERROR: 'Access denied. Insufficient permissions.',
      RATE_LIMIT_ERROR: 'Rate limit exceeded. Please try again later.',
      SERVER_ERROR: 'Server error. Using fallback data.',
      UNKNOWN_ERROR: 'An unexpected error occurred. Using fallback data.'
    },
    
    // Fallback data for different scenarios
    fallbackData: {
      // No hardcoded data - all data comes from API
    },
    
    // Error handling methods
    handleError: function(error, context = '') {
      console.error(`❌ Error in ${context}:`, error);
      
      let errorType = 'UNKNOWN_ERROR';
      let fallbackData = null;
      
      // Determine error type and appropriate fallback
      if (error.message?.includes('Network') || error.message?.includes('fetch')) {
        errorType = 'NETWORK_ERROR';
        fallbackData = this.getFallbackData();
      } else if (error.message?.includes('API') || error.message?.includes('HTTP')) {
        errorType = 'API_ERROR';
        fallbackData = this.getFallbackData();
      } else if (error.message?.includes('parse') || error.message?.includes('JSON')) {
        errorType = 'PARSE_ERROR';
        fallbackData = this.getFallbackData();
      } else if (error.message?.includes('validation') || error.message?.includes('invalid')) {
        errorType = 'VALIDATION_ERROR';
        fallbackData = this.getFallbackData();
      } else if (error.message?.includes('timeout')) {
        errorType = 'TIMEOUT_ERROR';
        return this.retryOperation(context);
      } else if (error.message?.includes('auth') || error.message?.includes('401')) {
        errorType = 'AUTH_ERROR';
        return this.handleAuthError();
      } else if (error.message?.includes('permission') || error.message?.includes('403')) {
        errorType = 'PERMISSION_ERROR';
        return this.handlePermissionError();
      } else if (error.message?.includes('rate limit') || error.message?.includes('429')) {
        errorType = 'RATE_LIMIT_ERROR';
        return this.handleRateLimitError();
      } else if (error.message?.includes('server') || error.message?.includes('500')) {
        errorType = 'SERVER_ERROR';
        fallbackData = this.getFallbackData();
      }
      
      // Set error state
      this.currentState = 'error';
      
      // Show user-friendly error message
      this.showErrorMessage(errorType, context);
      
      // Return fallback data if available
      if (fallbackData) {
        this.currentState = 'fallback';
        return fallbackData;
      }
      
      return null;
    },
    
    // Get fallback data based on current context
    getFallbackData: function() {
      // No fallback data - all data comes from API
      return null;
    },
    
    // Show user-friendly error message
    showErrorMessage: function(errorType, context) {
      const message = this.messages[errorType] || this.messages.UNKNOWN_ERROR;
      console.warn(`⚠️ ${message} (Context: ${context})`);
      
      // You could also show a toast notification here
      // toast.warn(message);
    },
    
    // Retry operation with exponential backoff
    retryOperation: function(context, maxRetries = 3) {
      return new Promise((resolve, reject) => {
        let retryCount = 0;
        
        const retry = () => {
          retryCount++;
          console.log(`🔄 Retrying ${context} (attempt ${retryCount}/${maxRetries})`);
          
          if (retryCount >= maxRetries) {
            console.error(`❌ Max retries exceeded for ${context}`);
            reject(new Error(`Max retries exceeded for ${context}`));
            return;
          }
          
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, retryCount - 1) * 1000;
          
          setTimeout(() => {
            // This would be replaced with the actual retry logic
            resolve(this.getFallbackData());
          }, delay);
        };
        
        retry();
      });
    },
    
    // Handle authentication errors
    handleAuthError: function() {
      console.error('🔐 Authentication failed');
      // Redirect to login or show login modal
      // window.location.href = '/login';
      return null;
    },
    
    // Handle permission errors
    handlePermissionError: function() {
      console.error('🚫 Access denied');
      // Show permission error message
      return null;
    },
    
    // Handle rate limit errors
    handleRateLimitError: function() {
      console.error('⏱️ Rate limit exceeded');
      // Show rate limit message and retry after delay
      return null;
    },
    
    // Reset error state
    reset: function() {
      this.currentState = 'success';
    }
  };
  
  // Enhanced data fetching with error handling and fallback
  const fetchBillingDataWithErrorHandling = async (userEmail, forceRefresh = false) => {
    try {
      ErrorHandler.reset();
      ErrorHandler.currentState = 'loading';
      
      const timestamp = forceRefresh ? '?_t=' + Date.now() : '';
      const url = `${API_BASE_URL}/api/billing/${encodeURIComponent(userEmail)}${timestamp}`;
      console.log('🔍 API URL being called:', url);
      console.log('🔍 DEBUG - About to fetch billing data for:', userEmail);
      
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Debug: Log the raw API response
      console.log('🔍 RAW API RESPONSE:', {
        hasData: !!data,
        dataType: typeof data,
        hasCalls: !!data?.calls,
        callsType: typeof data?.calls,
        callsLength: data?.calls?.length,
        hasSummary: !!data?.summary,
        summaryType: typeof data?.summary,
        firstCall: data?.calls?.[0] ? {
          call_id: data.calls[0].call_id,
          total_cost: data.calls[0].total_cost,
          has_id: !!data.calls[0].id
        } : null,
        // Show the actual data structure
        actualData: data,
        // Show the calls array specifically
        callsArray: data?.calls,
        // Show if calls is an array
        isCallsArray: Array.isArray(data?.calls),
        // Show all keys in the data object
        dataKeys: data ? Object.keys(data) : [],
        // Show the complete data object
        fullData: JSON.stringify(data, null, 2)
      });
      
      // Validate response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response: not an object');
      }
      
      if (!Array.isArray(data.calls)) {
        throw new Error('Invalid response: calls is not an array');
      }
      
      if (!data.summary || typeof data.summary !== 'object') {
        throw new Error('Invalid response: summary is not an object');
      }
      
      // Validate individual call data (more lenient validation)
      console.log('🔍 VALIDATING CALLS:', {
        totalCalls: data.calls.length,
        firstCall: data.calls[0] ? {
          call_id: data.calls[0].call_id,
          total_cost: data.calls[0].total_cost,
          id: data.calls[0].id
        } : null
      });
      
      for (const call of data.calls) {
        // Only validate if call_id exists and is not empty
        if (call.call_id && typeof call.call_id !== 'string') {
          console.error('❌ VALIDATION FAILED - call_id not string:', call.call_id);
          throw new Error('Invalid call data: call_id is not a string');
        }
        
        // Only validate total_cost if it exists
        if (call.total_cost !== undefined && call.total_cost !== null) {
          if (typeof call.total_cost !== 'number' && typeof call.total_cost !== 'string') {
            console.error('❌ VALIDATION FAILED - total_cost not number/string:', call.total_cost);
            throw new Error('Invalid call data: total_cost is not a number or string');
          }
        }
      }
      
      console.log('✅ VALIDATION PASSED - all calls are valid');
      console.log('🔍 FETCH BILLING DATA - Returning calls length:', data.calls?.length);
      console.log('🔍 FETCH BILLING DATA - First few calls:', data.calls?.slice(0, 3).map(call => ({
        call_id: call.call_id,
        total_cost: call.total_cost,
        id: call.id
      })));
      
      ErrorHandler.currentState = 'success';
      return data;
      
    } catch (error) {
      console.error('❌ FETCH BILLING DATA ERROR:', {
        error: error.message,
        stack: error.stack,
        userEmail: userEmail,
        forceRefresh: forceRefresh
      });
      
      const fallbackData = ErrorHandler.handleError(error, 'fetchBillingData');
      
      console.log('🔍 FALLBACK DATA:', {
        hasFallbackData: !!fallbackData,
        fallbackDataType: typeof fallbackData,
        fallbackData: fallbackData
      });
      
      if (fallbackData) {
        // Convert fallback data to expected format
        console.log('🔄 USING FALLBACK DATA');
        return {
          calls: [fallbackData],
          summary: {
            total_calls: 1,
            total_cost: fallbackData.total * 100, // Convert to cents
            total_duration_seconds: fallbackData.items.reduce((total, item) => 
              total + (item.duration || 0), 0),
            last_updated: new Date().toISOString()
          },
          cache_info: {
            generated_at: new Date().toISOString(),
            cache_control: 'fallback',
            etag: '"fallback-' + Date.now() + '"'
          }
        };
      }
      
      throw error;
    }
  };

  // SIMPLIFIED: Fetch billing data using ONLY the correct API endpoint
  const fetchBillingDataWithNewAPI = async (userId, forceRefresh = false, month = null, year = null) => {
    try {
      console.log('🚀 [BILLING] Fetching data for user ID:', userId, month ? `(Month: ${month}, Year: ${year})` : '(Current billing period)');
      
      // Force cache busting
      const timestamp = Date.now();
      let url = `${API_BASE_URL}/api/billing/calculate/${userId}?_t=${timestamp}`;
      
      // Add month and year parameters if provided
      if (month !== null && year !== null) {
        url += `&month=${month}&year=${year}`;
      }
      
      console.log('🔗 [BILLING] API URL:', url);
      
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 [BILLING] Raw API Response:', data);
      
      // Validate response
      if (!data.success || !data.calculation) {
        console.error('❌ [BILLING] Invalid API response structure:', data);
        throw new Error('Invalid API response structure');
      }
      
      // Extract the correct total cost
      const totalCost = data.calculation.total_cost || 0;
      console.log('💰 [BILLING] Total Cost from API:', totalCost);
      console.log('💰 [BILLING] Component A (Voice):', data.calculation.component_a?.amount || 0);
      console.log('💰 [BILLING] Component B (Chat):', data.calculation.component_b?.amount || 0);
      console.log('💰 [BILLING] Component C (Phone):', data.calculation.component_c?.amount || 0);
      console.log('💬 [BILLING] Component B details:', {
        amount: data.calculation.component_b?.amount,
        amountType: typeof data.calculation.component_b?.amount,
        amountToFixed4: data.calculation.component_b?.amount?.toFixed?.(4),
        amountToFixed6: data.calculation.component_b?.amount?.toFixed?.(6),
        records: data.calculation.component_b?.records?.length || 0,
        firstRecord: data.calculation.component_b?.records?.[0]
      });
      console.log('💰 [BILLING] Raw calculation object:', data.calculation);
      console.log('💰 [BILLING] Type of total_cost:', typeof totalCost);
      
      // Create simplified data structure
      // IMPORTANT: Preserve the calculation object as it contains the source of truth for Component A, B, C
      const result = {
        calls: data.calculation.component_a?.records || [],
        summary: {
          total_cost: totalCost, // API already returns dollars, no conversion needed
          voice_calls: data.calculation.component_a?.amount || 0,
          chat_sessions: data.calculation.component_b?.amount || 0,
          phone_numbers: data.calculation.component_c?.amount || 0 // Use calculation.component_c.amount (source of truth)
        },
        chatSessions: data.calculation.component_b?.records || [],
        phoneNumbers: data.calculation.component_c?.records || [],
        total_cost_dollars: totalCost, // Direct access to total cost
        calculation: data.calculation // Preserve the calculation object for direct access to Component A, B, C amounts
      };
      
      console.log('✅ [BILLING] Final Result:', result);
      console.log('✅ [BILLING] Component C from calculation:', result.calculation?.component_c?.amount);
      return result;
      
    } catch (error) {
      console.error('❌ [BILLING] Error:', error.message);
      throw error; // Don't use fallback data - let it fail clearly
    }
  };

  // Enhanced refresh function with error handling (using new API)
  const refreshBillingDataWithErrorHandling = async (userEmail, forceRefresh = false) => {
    try {
    if (!REFRESH_ENABLED && !forceRefresh) {
      console.log('⏹️ Refresh disabled by flag');
      return;
    }
    if (isRefreshing && !forceRefresh) {
      console.log('⏳ Refresh already in progress; skipping');
      return;
    }
      console.log(`🔄 Refreshing billing data for ${userEmail} (force: ${forceRefresh})`);
    // Prevent showing stale data from a previous user/session before the new API
    // response arrives. When force refreshing (e.g., after login/user switch),
    // reset all derived billing state so the UI doesn't render old amounts.
    if (forceRefresh && CLEAR_ON_REFRESH) {
      setApiBillingData(null);
      setApiPhoneNumbersData(null);
      setApiChatSessionsData(null);
      setExactComponentBAmount(null);
      setApiVoiceCallsData(null);
      setCallDetails([]);
      setTotalAmount(0);
    }
      
      // Try to get user ID first
      const userId = getUserId();
      if (userId) {
        console.log('🔄 Using new API with user ID:', userId);
        const data = await fetchBillingDataWithNewAPI(userId, forceRefresh);
        
        if (data && data.calls && Array.isArray(data.calls)) {
          // Change-detection: compute a lightweight hash; skip updates if unchanged
          const computeHash = (payload) => {
            try {
              const snapshot = {
                total: payload.total_cost_dollars || payload.summary?.total_cost || 0,
                a: payload.summary?.voice_calls || 0,
                b: payload.summary?.chat_sessions || 0,
                c: payload.summary?.phone_numbers || 0,
                calls: payload.calls?.length || 0,
                duration: payload.metadata?.total_duration_seconds || 0
              };
              const str = JSON.stringify(snapshot);
              let hash = 0;
              for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash |= 0;
              }
              return String(hash);
            } catch {
              return String(Date.now());
            }
          };
          const newHash = computeHash(data);
          const prevHash = localStorage.getItem('billing_data_hash');
          if (!forceRefresh && prevHash && prevHash === newHash) {
            console.log('⏹️ No billing changes detected; skipping state updates');
            // Still update last refresh timestamp to suppress extra polling churn
            localStorage.setItem('billing_last_refresh', new Date().toISOString());
            return data;
          }
          localStorage.setItem('billing_data_hash', newHash);
          setCallDetails(data.calls);
          
      // Store the complete API response data including calculation object
      // Ensure calculation object is stored for Component A, B, C access
      const billingDataWithCalculation = {
        ...data,
        calculation: data.calculation || null
      };
      setApiBillingData(billingDataWithCalculation);
      
      // Log calculation object for debugging
      if (data.calculation) {
        console.log('💰 [API] Storing calculation object:', {
          component_a: data.calculation.component_a?.amount || 0,
          component_b: data.calculation.component_b?.amount || 0,
          component_c: data.calculation.component_c?.amount || 0,
          total_cost: data.calculation.total_cost || 0
        });
      }
      
      // Debug: Log the service breakdown data
      console.log('🔧 [SERVICE BREAKDOWN] API Response service_breakdown:', data.service_breakdown);
      console.log('🔧 [SERVICE BREAKDOWN] API Response metadata:', data.metadata);
      console.log('🔧 [SERVICE BREAKDOWN] Full API Response structure:', Object.keys(data));
          
          // Store Component C (Phone Numbers) data from API
          if (data.phoneNumbers && data.phoneNumbers.length > 0) {
            setApiPhoneNumbersData(data.phoneNumbers);
            console.log('📱 [BILLING] Component C (Phone Numbers) data stored:', data.phoneNumbers.length, 'records');
          }
          
          // Store Component B (Chat Sessions) data from API for Text Testing
          if (data.chatSessions && data.chatSessions.length > 0) {
            setApiChatSessionsData(data.chatSessions);
            console.log('💬 [BILLING] Component B (Chat Sessions) data stored:', data.chatSessions.length, 'records');
          }
          
          // Also store the Component B amount directly for Text Testing
          if (data.calculation && data.calculation.component_b) {
            console.log('💬 [BILLING] Component B amount from API:', data.calculation.component_b.amount);
            setExactComponentBAmount(data.calculation.component_b.amount);
            // Store this in the apiBillingData for easy access
          if (data.summary) {
              data.summary.component_b_amount = data.calculation.component_b.amount;
            }
          }
          
          if (data.summary) {
            // Use direct total cost in dollars if available, otherwise use summary total cost (already in dollars)
            const baseAmount = data.total_cost_dollars || data.summary.total_cost;
            console.log('💰 Setting total amount from new API:', baseAmount);
            console.log('💰 Data summary:', data.summary);
            console.log('💰 Total cost dollars:', data.total_cost_dollars);
            setTotalAmount(baseAmount);
          }
          
          // Set chat sessions and phone numbers if available
          if (data.chatSessions && Array.isArray(data.chatSessions)) {
            console.log('📊 Chat sessions loaded:', data.chatSessions.length);
          }
          
          if (data.phoneNumbers && Array.isArray(data.phoneNumbers)) {
            console.log('📱 Phone numbers loaded:', data.phoneNumbers.length);
          }
          
          // Update last refresh timestamp
          localStorage.setItem('billing_last_refresh', new Date().toISOString());
          
          console.log(`✅ Billing data refreshed successfully with new API (${data.calls.length} calls)`);
          return data;
        } else {
          console.warn('⚠️ Invalid data structure received from new API');
        }
      } else {
        console.error('❌ NO USER ID FOUND - CANNOT USE OLD API FALLBACK');
        throw new Error('No user ID found - cannot fetch billing data');
      }
      
      // COMMENTED OUT: Old API fallback - causes incorrect billing amounts
      // const data = await fetchBillingDataWithErrorHandling(userEmail, forceRefresh);
      // 
      // if (data && data.calls && Array.isArray(data.calls)) {
      //   setCallDetails(data.calls);
      //   
      //   if (data.summary) {
      //     const baseAmount = data.summary.total_cost / 100; // Backend returns cents, convert to dollars
      //     const totalWithPhoneNumbers = calculateTotalAmountWithPhoneNumbers(baseAmount);
      //     setTotalAmount(totalWithPhoneNumbers);
      //   }
      //   
      //   // Update last refresh timestamp
      //   localStorage.setItem('billing_last_refresh', new Date().toISOString());
      //   
      //   console.log(`✅ Billing data refreshed successfully with fallback API (${data.calls.length} calls)`);
      //   return data;
      // } else {
      //   throw new Error('Invalid data structure received');
      // }
    } catch (error) {
      const fallbackData = ErrorHandler.handleError(error, 'refreshBillingData');
      
      if (fallbackData) {
        // Apply fallback data
        setCallDetails([fallbackData]);
        setTotalAmount(fallbackData.total);
        
        console.log(`⚠️ Using fallback data for billing`);
        return fallbackData;
      }
      
      throw error;
    }
  };
  
  
  // Auto-refresh mechanism
  const setupAutoRefresh = (userEmail) => {
    if (!AUTO_REFRESH_ENABLED || !REFRESH_ENABLED) {
      console.log('⏹️ Auto-refresh disabled');
      return;
    }
    // Clear any existing interval
    if (window.billingRefreshInterval) {
      clearInterval(window.billingRefreshInterval);
    }
    
    // Set up auto-refresh every 60 minutes
    window.billingRefreshInterval = setInterval(async () => {
      try {
        const lastRefresh = localStorage.getItem('billing_last_refresh');
        const now = new Date();
        const lastRefreshTime = lastRefresh ? new Date(lastRefresh) : new Date(0);
        const timeDiff = now - lastRefreshTime;
        
        // Only refresh if it's been more than 55 minutes
        if (timeDiff > 55 * 60 * 1000) {
          await refreshBillingDataWithErrorHandling(userEmail, false);
        }
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, 60 * 60 * 1000); // 60 minutes
  };
  
  // Cleanup function
  const cleanupAutoRefresh = () => {
    if (window.billingRefreshInterval) {
      clearInterval(window.billingRefreshInterval);
      window.billingRefreshInterval = null;
    }
  };
  
  // Helper to get user email (copied from CallHistory.jsx)
  const getUserEmail = () => {
    try {
      const user = JSON.parse(localStorage.getItem('chatbotUser'));
      const email = user?.email || null;
      // Fix email case to match database
      if (email === 'peter@urcommunity.com.au') {
        return 'Peter@urcommunity.com.au';
      }
      return email;
    } catch {
      return null;
    }
  };

  // Helper to get user ID for the new billing calculation API
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('chatbotUser'));
      return user?.userId || user?.id || null;
    } catch {
      return null;
    }
  };

  // Function to fetch billing data for all months for the logged-in user
  const fetchAllMonthlyBillingData = async (userId) => {
    if (!userId) {
      console.log('⚠️ [MONTHLY BILLING] No user ID available');
      return;
    }

    try {
      console.log('🔄 [MONTHLY BILLING] Fetching billing data for all months...');
      
      // Get current date and calculate months from first data date to current month
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      // Start from 12 months ago to ensure we get all historical data (including October 2025)
      // This ensures we capture all billing periods that might have data
      const startDate = new Date(currentYear, currentMonth - 12, 1);
      const months = [];
      
      // Generate all months from start date to current month
      let date = new Date(startDate);
      while (date <= currentDate) {
        months.push({
          year: date.getFullYear(),
          month: date.getMonth() + 1
        });
        date.setMonth(date.getMonth() + 1);
      }
      
      console.log(`📅 [MONTHLY BILLING] Testing ${months.length} billing periods`);
      
      const monthlyData = {};
      
      // Fetch billing data for each month
      for (const { year, month } of months) {
        const monthYear = `${year}-${String(month).padStart(2, '0')}`;
        
        try {
          const data = await fetchBillingDataWithNewAPI(userId, false, month, year);
          
          if (data && data.summary) {
            // ALWAYS use calculation.component_c.amount directly - this is the source of truth from API
            // The API returns the correct value in calculation.component_c.amount ($42)
            // NEVER use data.summary.phone_numbers as it might be incorrect ($84)
            // The calculation object is the authoritative source
            const componentCAmount = data.calculation?.component_c?.amount ?? 0;
            const componentCCount = data.calculation?.component_c?.count ?? data.phoneNumbers?.length ?? 0;
            
            console.log(`🔍 [MONTHLY BILLING] ${monthYear} - Component C from API:`, {
              calculation_component_c_amount: data.calculation?.component_c?.amount,
              calculation_component_c_count: data.calculation?.component_c?.count,
              summary_phone_numbers: data.summary?.phone_numbers,
              phoneNumbers_length: data.phoneNumbers?.length,
              using_amount: componentCAmount,
              using_count: componentCCount,
              note: 'Using calculation.component_c.amount as source of truth'
            });
            
            monthlyData[monthYear] = {
              componentA: {
                amount: data.summary.voice_calls || 0,
                count: data.calls?.length || 0
              },
              componentB: {
                amount: data.summary.chat_sessions || 0,
                count: data.chatSessions?.length || 0
              },
              componentC: {
                amount: Number(componentCAmount || 0), // Use calculation.component_c.amount (source of truth)
                count: componentCCount
              },
              totalCost: data.summary.total_cost || 0
            };
            
            console.log(`✅ [MONTHLY BILLING] ${monthYear}: A=$${monthlyData[monthYear].componentA.amount.toFixed(2)}, B=$${monthlyData[monthYear].componentB.amount.toFixed(2)}, C=$${monthlyData[monthYear].componentC.amount.toFixed(2)} (${monthlyData[monthYear].componentC.count} records)`);
          }
        } catch (error) {
          console.error(`❌ [MONTHLY BILLING] Error fetching ${monthYear}:`, error.message);
          // Continue with other months even if one fails
        }
      }
      
      setMonthlyBillingData(monthlyData);
      console.log(`✅ [MONTHLY BILLING] Fetched data for ${Object.keys(monthlyData).length} months`);
      
    } catch (error) {
      console.error('❌ [MONTHLY BILLING] Error fetching all monthly billing data:', error);
    }
  };


  // Function to fetch billing status by month from server
  const fetchBillingStatusByMonth = async (userEmail) => {
    try {
      console.log('🔄 Fetching billing status for user:', userEmail);
      const response = await fetch(`${API_BASE_URL}/api/billing/${encodeURIComponent(userEmail)}/status`);
      
      if (!response.ok) {
        console.log('⚠️ Billing status endpoint not available (404), skipping...');
        return; // Don't overwrite existing data
      }
      
      const data = await response.json();
      console.log('📊 Server response:', data);
      
      if (data.billing_status_by_month) {
        setBillingStatusByMonth(data.billing_status_by_month);
        console.log('📊 Billing status by month fetched:', data.billing_status_by_month.length);
        console.log('📋 All billing status data:', data.billing_status_by_month);
        
        // Debug: Log each month's status
        data.billing_status_by_month.forEach(item => {
          console.log(`📅 ${item.month_year}: ${item.billing_status} (${item.total_calls} calls)`);
        });
      } else {
        console.log('⚠️ No billing_status_by_month in response');
      }
    } catch (error) {
      console.log('⚠️ Billing status endpoint not available, skipping...');
      // Don't log as error since this endpoint doesn't exist
    }
  };

  // Function to fetch phone number billing data by month from server
  const fetchPhoneNumberBilling = async (userEmail) => {
    try {
      console.log('🔄 Fetching phone number billing for user:', userEmail);
      const response = await fetch(`${API_BASE_URL}/api/billing/${encodeURIComponent(userEmail)}/phone-numbers`);
      
      if (!response.ok) {
        console.log('⚠️ Phone number billing endpoint not available (404), skipping...');
        return; // Don't overwrite existing data
      }
      
      const data = await response.json();
      console.log('📞 Phone number billing response:', data);
      
      if (data.phone_number_billing) {
        setPhoneNumberBilling(data.phone_number_billing);
        console.log('📞 Phone number billing fetched:', data.phone_number_billing.length);
        // REMOVED: Detailed logging to avoid confusion with API data
        // console.log('📋 All phone number billing data:', data.phone_number_billing);
        
        // REMOVED: Individual month logging to avoid confusion
        // data.phone_number_billing.forEach(item => {
        //   console.log(`📅 ${item.month_year}: ${item.phone_count} numbers, $${item.total_cost}`);
        // });
      } else {
        console.log('⚠️ No phone_number_billing in response');
      }
    } catch (error) {
      console.log('⚠️ Phone number billing endpoint not available, skipping...');
      // Don't log as error since this endpoint doesn't exist
    }
  };

  // Function to fetch monthly totals from server (combined from call_logs and ai_assistant_call_logs)
  const fetchMonthlyTotals = async (userEmail) => {
    try {
      console.log('🔄 Fetching combined monthly totals for user:', userEmail);
      const response = await fetch(`${API_BASE_URL}/api/billing/combined-monthly-totals/${encodeURIComponent(userEmail)}`);
      
      if (!response.ok) {
        console.log('⚠️ Combined monthly totals endpoint not available (404), skipping...');
        return; // Don't overwrite existing data
      }
      
      const data = await response.json();
      console.log('📊 Combined monthly totals response:', data);
      
      if (data.monthly_data) {
        setMonthlyTotals(data.monthly_data);
        console.log('📊 Combined monthly totals fetched:', data.monthly_data.length);
        // REMOVED: Detailed logging to avoid confusion with API data
        // console.log('📋 All combined monthly totals data:', data.monthly_data);
        
        // REMOVED: Individual month logging to avoid confusion
        // data.monthly_data.forEach(item => {
        //   console.log(`📅 ${item.month_year}: $${parseFloat(item.total_cost).toFixed(2)} (${item.total_calls} calls)`);
        // });
      } else {
        console.log('⚠️ No monthly_data in response');
      }
    } catch (error) {
      console.log('⚠️ Combined monthly totals endpoint not available, skipping...');
      // Don't log as error since this endpoint might not exist
    }
  };

  // Function to fetch Stripe invoices with enhanced auto-sync logic
  const fetchStripeInvoices = async (userEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe-invoices/${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      
      if (data.success && data.invoices) {
        const previousInvoiceCount = stripeInvoices.length;
        const previousInvoiceIds = new Set(stripeInvoices.map(inv => inv.id));
        
        setStripeInvoices(data.invoices);
        console.log('📄 Stripe invoices fetched:', data.invoices.length);
        console.log('📋 Sample invoice:', data.invoices[0]);
        
        // Check for new invoices by comparing IDs
        const newInvoices = data.invoices.filter(invoice => !previousInvoiceIds.has(invoice.id));
        if (newInvoices.length > 0) {
          console.log(`🔄 ${newInvoices.length} new invoices detected, auto-syncing to database...`);
          console.log('📄 New invoice IDs:', newInvoices.map(inv => inv.id));
          await autoSyncToDatabase(userEmail);
        } else if (data.invoices.length > previousInvoiceCount) {
          console.log('🔄 Invoice count increased, auto-syncing to database...');
          await autoSyncToDatabase(userEmail);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching Stripe invoices:', error);
    }
  };

  // Function to auto-sync invoices to database with enhanced functionality
  const autoSyncToDatabase = async (userEmail) => {
    try {
      console.log('🔄 Starting auto-sync for user:', userEmail);
      const response = await fetch(`${API_BASE_URL}/api/sync-stripe-invoices/${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await response.json();
      if (data.success) {
        console.log('✅ Auto-sync completed:', data.message);
        console.log('📊 Updated months:', data.updatedMonths || []);
        
        // Only refresh billing data if we don't already have good data
        if (callDetails.length === 0) {
          await refreshBillingDataWithErrorHandling(userEmail, true);
        } else {
          console.log('📊 Skipping billing data refresh - already have data');
        }
        
        // Also refresh billing status, phone number billing, and Stripe invoices
        await fetchBillingStatusByMonth(userEmail);
        await fetchPhoneNumberBilling(userEmail);
        await fetchStripeInvoices(userEmail);
        
        // Show success notification if there were updates
        if (data.updatedMonths && data.updatedMonths.length > 0) {
          console.log(`✅ Synced ${data.updatedMonths.length} months with invoice links`);
        }
      } else {
        console.log('ℹ️ No updates needed during auto-sync');
      }
    } catch (error) {
      console.error('❌ Error in auto-sync:', error);
    }
  };

  // Function to refresh billing data without page reload

  // Debug function to check what data exists
  const debugUserData = async (userEmail) => {
    try {
      console.log('🔍 [DEBUG] Checking user data for:', userEmail);
      
      const response = await fetch(`${API_BASE_URL}/api/billing/debug/${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🔍 [DEBUG] Debug API Response:', data);
      
      if (data.success) {
        console.log('🔍 [DEBUG] User exists:', data.user_exists);
        console.log('🔍 [DEBUG] User ID:', data.user_id);
        console.log('🔍 [DEBUG] Call statistics:', data.call_statistics);
        console.log('🔍 [DEBUG] Recent calls:', data.recent_calls);
        console.log('🔍 [DEBUG] September calls:', data.september_2025_calls);
        console.log('🔍 [DEBUG] All months with data:', data.all_months_with_data);
        console.log('🔍 [DEBUG] Sample call:', data.debug_info.sample_call);
        
        // Show alert with debug info
        alert(`Debug Info for ${userEmail}:
        
User exists: ${data.user_exists}
User ID: ${data.user_id}
Total calls: ${data.call_statistics.total_calls}
Processed calls: ${data.call_statistics.processed_calls}
Pending calls: ${data.call_statistics.pending_calls}
Null status calls: ${data.call_statistics.null_status_calls}
Calls with cost: ${data.call_statistics.calls_with_cost}
Total cost sum: $${data.call_statistics.total_cost_sum}

September 2025 calls: ${data.debug_info.september_calls_found}
Months with data: ${data.debug_info.months_with_data}

Check browser console for detailed logs.`);
      } else {
        console.error('❌ [DEBUG] API returned error:', data.error);
        alert(`Debug Error: ${data.error}`);
      }
      
    } catch (error) {
      console.error('❌ [DEBUG] Error:', error);
      alert(`Debug Error: ${error.message}`);
    }
  };

  // September inspection function
  const inspectSeptemberData = async (userEmail) => {
    try {
      console.log('🔍 [SEPTEMBER-INSPECT] Inspecting September 2025 data for:', userEmail);
      
      const response = await fetch(`${API_BASE_URL}/api/billing/inspect-september/${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🔍 [SEPTEMBER-INSPECT] Inspection API Response:', data);
      
      if (data.success) {
        const results = data.inspection_results;
        console.log('🔍 [SEPTEMBER-INSPECT] September inspection results:', results);
        
        // Show detailed alert
        alert(`September 2025 Inspection for ${userEmail}:
        
Total September calls (all users): ${results.total_september_calls_all_users}
Your September calls: ${results.user_september_calls}
Calls with weird dates: ${results.weird_date_calls}

User September calls data:
${results.user_september_calls_data.map(call => 
  `- ${call.formatted_date}: ${call.call_id} (Status: ${call.billing_status || 'NULL'}, Cost: $${call.total_cost || 'NULL'})`
).join('\n')}

Check browser console for complete data.`);
      } else {
        console.error('❌ [SEPTEMBER-INSPECT] API returned error:', data.error);
        alert(`September Inspection Error: ${data.error}`);
      }
      
    } catch (error) {
      console.error('❌ [SEPTEMBER-INSPECT] Error:', error);
      alert(`September Inspection Error: ${error.message}`);
    }
  };

  const refreshBillingData = async (userEmail, forceRefresh = true) => {
    try {
      setIsRefreshing(true);
      console.log(`🔄 REFRESH DEBUG - Starting refresh for ${userEmail}, forceRefresh: ${forceRefresh}`);
      
      // Try to get user ID first
      const userId = getUserId();
      let data;
      
      if (userId) {
        console.log('🔄 REFRESH: Using NEW API with user ID:', userId);
        data = await fetchBillingDataWithNewAPI(userId, forceRefresh);
      } else {
        console.error('❌ REFRESH: No user ID found - CANNOT use old API');
        throw new Error('No user ID found - cannot fetch billing data');
      }
      
      console.log('🔄 REFRESH DEBUG - API Response:', {
        hasCalls: !!data.calls,
        callsLength: data.calls?.length,
        firstCall: data.calls?.[0] ? {
          call_id: data.calls[0].call_id,
          total_cost: data.calls[0].total_cost,
          has_call_cost: !!data.calls[0].call_cost
        } : null,
        summary: data.summary
      });
      
      if (data.calls && Array.isArray(data.calls)) {
        console.log('🔍 REFRESH DEBUG - About to set callDetails with:', {
          callsLength: data.calls.length,
          firstCall: data.calls[0] ? {
            call_id: data.calls[0].call_id,
            total_cost: data.calls[0].total_cost
          } : null
        });
        setCallDetails(data.calls);
        
        if (data.summary) {
          if (userId) {
            // New API: Use direct total cost in dollars if available, otherwise use summary total cost (already in dollars)
            const baseAmount = data.total_cost_dollars || data.summary.total_cost;
            console.log('💰 REFRESH: Setting total amount from new API:', baseAmount);
            setTotalAmount(baseAmount);
          } else {
            // COMMENTED OUT: Old API logic - causes double counting of phone numbers
            // The new API already includes phone number costs, so we don't add them again
            console.log('💰 REFRESH: Using new API data directly (no phone number addition)');
            const baseAmount = data.total_cost_dollars || data.summary.total_cost;
            setTotalAmount(baseAmount);
          }
        }
        console.log(`✅ Billing data refreshed without page reload (${data.calls?.length} calls)`);
      } else {
        console.error('❌ REFRESH DEBUG - No calls data received:', data);
      }
    } catch (error) {
      console.error('❌ Error refreshing billing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

    
    // Setup auto-refresh on component mount
  useEffect(() => {
    // Ensure any prior intervals are cleared to prevent repeated refreshes
    cleanupAutoRefresh();
    const userEmail = getUserEmail();
    const userId = getUserId();
    
    if (userEmail) {
      // Initial data loading
      const loadInitialData = async () => {
        try {
          console.log('🔄 Loading initial billing data...');
          console.log('👤 User Email:', userEmail);
          console.log('🆔 User ID:', userId);
          
          // Use new API if user ID is available
          if (userId) {
            console.log('🔄 Using new billing calculation API...');
            await refreshBillingDataWithErrorHandling(userEmail, true);
            // Fetch billing data for all months
            await fetchAllMonthlyBillingData(userId);
          } else {
            console.log('🔄 Using legacy API (no user ID)...');
            await refreshBillingDataWithErrorHandling(userEmail, true);
          }
          
          await fetchBillingStatusByMonth(userEmail);
          await fetchPhoneNumberBilling(userEmail);
          await fetchMonthlyTotals(userEmail);
          await fetchStripeInvoices(userEmail);
          console.log('✅ Initial data loading completed');
        } catch (error) {
          console.error('❌ Initial data loading failed:', error);
        }
      };
      
      // Guard against dev double-invocation
      if (didInitRef.current) {
        return;
      }
      didInitRef.current = true;
      loadInitialData();
      setupAutoRefresh(userEmail);
      
      // Cleanup on unmount
      return () => {
        cleanupAutoRefresh();
      };
    }
  }, []);

  // DISABLED: This useEffect was causing race conditions with the refresh function
  // The refresh function now handles all data fetching properly
  /*
  useEffect(() => {
    const userEmail = getUserEmail();
    const userId = getUserId();
    
    if (userEmail && !isRefreshing) {
      // Use new API if user ID is available
      if (userId) {
        console.log('🔄 useEffect: Using new API with user ID:', userId);
        fetchBillingDataWithNewAPI(userId, false)
          .then(data => {
            if (data && data.calls && Array.isArray(data.calls)) {
              setCallDetails(data.calls);
              
              if (data.summary) {
                // Use direct total cost in dollars if available, otherwise use summary total cost (already in dollars)
                const baseAmount = data.total_cost_dollars || data.summary.total_cost;
                console.log('💰 useEffect: Setting total amount from new API:', baseAmount);
                setTotalAmount(baseAmount);
              }
            }
          })
          .catch(error => {
            console.error('❌ useEffect: New API error:', error);
            // COMMENTED OUT: Old API fallback - causes incorrect billing amounts
            // fetchBillingDataWithErrorHandling(userEmail)
            //   .then(data => {
            //     if (data.calls && Array.isArray(data.calls)) {
            //       setCallDetails(data.calls);
            //       if (data.summary) {
            //         const baseAmount = data.summary.total_cost / 100;
            //         const totalWithPhoneNumbers = calculateTotalAmountWithPhoneNumbers(baseAmount);
            //         setTotalAmount(totalWithPhoneNumbers);
            //       }
            //     }
            //   });
          });
      } else {
        console.error('❌ useEffect: No user ID found - CANNOT use old API');
        // COMMENTED OUT: Old API fallback - causes incorrect billing amounts
        // COMMENTED OUT: Old API call - causes incorrect billing amounts
        // fetchBillingDataWithErrorHandling(userEmail)
        //   .then(data => {
        //     if (data.calls && Array.isArray(data.calls)) {
        //       setCallDetails(data.calls);
        //       if (data.summary) {
        //         const baseAmount = data.summary.total_cost / 100;
        //         const totalWithPhoneNumbers = calculateTotalAmountWithPhoneNumbers(baseAmount);
        //         setTotalAmount(totalWithPhoneNumbers);
        //       }
        //     }
        //     
        //     // Debug: Log billing status data
        //     console.log('📊 Billing data received:', data.calls.length, 'calls');
        //     console.log('📊 Summary data:', data.summary);
        //     console.log('📋 Sample call data:', data.calls.slice(0, 3).map(call => ({
        //       id: call.id,
        //       created_at: call.created_at,
        //       billing_status: call.billing_status,
        //       pdf_link: call.pdf_link
        //     })));
        //     
        //     // Debug: Log all billing statuses from server
        //     const allBillingStatuses = data.calls.map(call => call.billing_status).filter(status => status);
        //     console.log('🎯 All billing statuses from server:', [...new Set(allBillingStatuses)]);
        //     
        //     // Debug: Log calls by month with their billing status
        //     const callsByMonth = {};
        //     data.calls.forEach(call => {
        //       const callDate = new Date(call.created_at);
        //       const monthYear = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
        //       if (!callsByMonth[monthYear]) {
        //         callsByMonth[monthYear] = [];
        //       }
        //       callsByMonth[monthYear].push({
        //         id: call.id,
        //         billing_status: call.billing_status,
        //         pdf_link: call.pdf_link
        //       });
        //     });
        //     console.log('📅 Calls grouped by month with billing status:', callsByMonth);
        //   }
        // })
        // .catch(error => {
        //   console.error('Error fetching billing data:', error);
        //   // Fallback to the old API if the new one fails
        //   fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}`)
        //     .then(res => res.json())
        //     .then(data => {
        //       if (Array.isArray(data)) {
        //         setCallDetails(data);
        //       }
        //     });
        // });
      
      // Fetch billing status by month from server
      fetchBillingStatusByMonth(userEmail);
      
      // Fetch phone number billing data from server
      fetchPhoneNumberBilling(userEmail);
      
      // Debug: Log current month format
      const currentDate = new Date();
      const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      console.log('📅 Current month format:', currentMonthYear);
      
      // Fetch Stripe invoices automatically
      fetchStripeInvoices(userEmail);
      
      // Enhanced auto-sync functionality - run after initial data load
      const performAutoSync = async () => {
        console.log('🔄 Performing automatic sync...');
        await autoSyncToDatabase(userEmail);
      };
      
      // Disable auto-sync for now as it's interfering with data display
      // setTimeout(() => {
      //   performAutoSync();
      // }, 2000); // Wait 2 seconds for initial data to load
      
      // Set up automatic refresh every 3 minutes (without auto-sync)
      const intervalId = setInterval(async () => {
        console.log('🔄 Auto-refreshing data...');
        await fetchBillingStatusByMonth(userEmail);
        await fetchPhoneNumberBilling(userEmail);
        await fetchStripeInvoices(userEmail);
        // Disabled auto-sync to prevent data interference
        // await autoSyncToDatabase(userEmail);
      }, 180000); // 3 minutes
      
      // Cleanup interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, []);
  */

  useEffect(() => {
    // Simulate fetching the upcoming invoice amount from an API
    // Replace this with your real API call
    setTimeout(() => {
      setUpcomingInvoice(17.65); // Example: fetched value
      // Don't override totalAmount here - it's already set from the API call
    }, 500);
  }, []);

  // Helper function to calculate total amount including phone numbers
  const calculateTotalAmountWithPhoneNumbers = (baseAmount) => {
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const phoneBillingData = getPhoneNumberBillingForMonth(currentMonthKey);
    const totalWithPhoneNumbers = baseAmount + phoneBillingData.cost;
    
    console.log('💰 Calculating total amount:', {
      baseAmount: baseAmount,
      phoneNumbersCost: phoneBillingData.cost,
      totalWithPhoneNumbers: totalWithPhoneNumbers
    });
    
    return totalWithPhoneNumbers;
  };

  const toggleRow = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

    // Generate billing history data with accordion structure
  const getCurrentMonthYear = () => {
    const now = new Date();
    return now.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Dynamic calculation functions for billing details
  const getVoiceEngineDetails = () => {
    // This should be replaced with actual API call to get voice engine usage
    const usageMinutes = 0; // Replace with actual data
    return `${usageMinutes} minutes`;
  };

  const getLLMDetails = () => {
    // This should be replaced with actual API call to get LLM usage
    const usageMinutes = 0; // Replace with actual data
    return `${usageMinutes} minutes`;
  };

  const getKnowledgeBaseDetails = () => {
    // This should be replaced with actual API call to get knowledge base usage
    const usageMinutes = 0; // Replace with actual data
    return `${usageMinutes} minutes`;
  };

  const getPhoneNumberDetails = () => {
    // This should be replaced with actual API call to get phone number count
    const phoneCount = 0; // Replace with actual data
    return `${phoneCount} numbers`;
  };

  const getTextTestingDetails = () => {
    // This should be replaced with actual API call to get text testing count
    const testCount = 0; // Replace with actual data
    return `${testCount} tests`;
  };

  const getTelephonyDetails = () => {
    // This should be replaced with actual API call to get telephony usage
    const usageMinutes = 0; // Replace with actual data
    return `${usageMinutes} minutes`;
  };

  const billingData = [];

  const handleDownload = (month) => {
    // Handle PDF download functionality
    console.log(`Downloading invoice for ${month}`);
    // Add your PDF download logic here
  };


  const renderAccordionCard = (item, level = 0) => {
    const isExpanded = expandedRows.has(item.id);
    const hasChildren = item.hasChildren && item.children && item.children.length > 0;

    // Format title if it matches YYYY-MM
    let displayTitle = item.title;
    if (/^\d{4}-\d{2}$/.test(item.title)) {
      // Always use 6th for the day display
      displayTitle = formatMonthYear(item.title, 6);
    }

    return (
      <div key={item.id} style={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px', 
        overflow: 'hidden',
        background: '#fff',
        marginBottom: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        {/* Accordion Header */}
        <div 
          style={{ 
            padding: '0', 
            background: '#fff',
            cursor: hasChildren ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: hasChildren && isExpanded ? '1px solid #f1f5f9' : 'none',
            transition: 'background-color 0.2s ease',
            minHeight: '56px',
          }}
          onClick={() => hasChildren && toggleRow(item.id)}
          onMouseEnter={(e) => hasChildren && (e.target.style.background = '#f9fafb')}
          onMouseLeave={(e) => hasChildren && (e.target.style.background = '#fff')}
        >
          {/* Title (with chevron if needed) */}
          <div style={{ display: 'flex', alignItems: 'center', flex: 2, gap: '12px', minWidth: 0 }}>
            {hasChildren && (
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} 
                 style={{ fontSize: '14px', color: '#374151', transition: 'transform 0.2s ease' }}></i>
            )}
            <div style={{ 
              paddingLeft: level > 0 ? `${level * 20}px` : '0px',
              fontSize: '15px',
              fontWeight: '500',
              color: '#374151',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1
            }}>
              {displayTitle}
            </div>
          </div>
          {/* Amount */}
          <div style={{ 
            flex: 1, 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#374151',
            minWidth: '60px',
            textAlign: 'right' 
          }}>
            ${item.amount.toFixed(2)}
          </div>
          {/* Details */}
          <div style={{ flex: 2, fontSize: '15px', color: '#6b7280', textAlign: 'left', paddingLeft: '12px' }}>
            {item.details || ''}
          </div>
          {/* Status */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            {item.status && (
              <>
                {item.status === 'Active' && (
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#3b82f6',
                    display: 'inline-block' 
                  }}></span>
                )}
                {item.status === 'Paid' && (
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#10b981',
                    display: 'inline-block' 
                  }}></span>
                )}
                <span style={{ 
                  color: item.status === 'Paid' ? '#10b981' : item.status === 'Active' ? '#3b82f6' : '#f59e0b',
                  fontWeight: '500',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {item.status === 'Active' ? 'Ongoing' : item.status}
                </span>
              </>
            )}
            {/* Invoice Button */}
            {item.type === 'summary' && item.status === 'Paid' && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(item.title);
                }}
                style={{ 
                  background: '#fff', 
                  border: '1px solid #d1d5db', 
                  cursor: 'pointer', 
                  color: '#374151',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#fff';
                  e.target.style.borderColor = '#d1d5db';
                }}
                title="Download Invoice"
              >
                <i className="fas fa-external-link-alt" style={{ fontSize: '11px' }}></i>
                Invoice
              </button>
            )}
          </div>
        </div>
        {/* Accordion Content */}
        {hasChildren && isExpanded && item.children && (
          <div style={{ 
            background: '#f8fafc',
            borderTop: '1px solid #f1f5f9',
            padding: '0 0 0 0'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {item.children.map(child => renderAccordionCard(child, level + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Utility function to add ordinal suffix to day numbers
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Utility function to format 'YYYY-MM' as 'MonthName Day, Year' with ordinal suffix
  const formatMonthYear = (monthYear, day = null) => {
    if (!monthYear || monthYear === '0') return 'Unknown Month';
    
    const [year, month] = monthYear.split('-');
    if (!year || !month) return 'Invalid Date';
    
    const date = new Date(Number(year), Number(month) - 1);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // If no specific day provided, just show month and year
    if (!day) {
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    
    // Add ordinal suffix to the day
    const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
    return `${ordinalDay} ${date.toLocaleString('default', { month: 'long' })} ${year}`;
  };

  // Add new renderAccordionTableRow function for table rows with accordion logic
  const renderAccordionTableRow = (item, level = 0) => {
    const isExpanded = expandedRows.has(item.id);
    const hasChildren = item.hasChildren && item.children && item.children.length > 0;
    // Format title if it matches YYYY-MM
    let displayTitle = item.title;
    if (/^\d{4}-\d{2}$/.test(item.title)) {
      // Always use 6th for the day display
      displayTitle = formatMonthYear(item.title, 6);
    }
    return (
      <React.Fragment key={item.id}>
        <tr
          style={{
            background: '#fff',
            borderBottom: '1px solid #f1f5f9',
            cursor: hasChildren ? 'pointer' : 'default',
            transition: 'background-color 0.2s ease',
          }}
          onClick={() => hasChildren && toggleRow(item.id)}
          onMouseEnter={e => hasChildren && (e.currentTarget.style.background = '#f9fafb')}
          onMouseLeave={e => hasChildren && (e.currentTarget.style.background = '#fff')}
        > 
          {/* Title with chevron and indentation */}
          <td style={{ padding: '14px 20px', fontWeight: 500, color: '#374151', fontSize: '15px', verticalAlign: 'middle' }}>
            <span style={{ paddingLeft: `${level * 24}px`, display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
              {hasChildren && (
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ fontSize: '14px', color: '#374151' }}></i>
              )}
              {displayTitle}
            </span>
          </td>
          {/* Amount */}
          <td style={{ padding: '14px 20px', fontWeight: 600, color: '#374151', fontSize: '16px', textAlign: 'right', verticalAlign: 'middle' }}>
            ${item.amount.toFixed(2)}
          </td>
          {/* Details */}
          <td style={{ padding: '14px 20px', color: '#6b7280', fontSize: '15px', verticalAlign: 'middle' }}>
            {item.details || ''}
          </td>
          {/* Status and Invoice Button */}
          <td style={{ padding: '14px 20px', verticalAlign: 'middle' }}>
            {item.type === 'summary' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Status with colored dot like Retell AI */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Colored dot */}
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: (() => {
                      const status = getBillingStatusForMonth(item.monthYear || item.title);
                      switch (status) {
                        case 'Paid': return '#10b981'; // Green
                        case 'Pending': return '#f59e0b'; // Orange
                        case 'No Status': return '#6b7280'; // Gray
                        default: return '#3b82f6'; // Blue
                      }
                    })()
                  }}></div>
                  {/* Status text */}
                  <span style={{
                    color: (() => {
                      const monthYear = extractMonthYear(item);
                      const status = getBillingStatusForMonth(monthYear);
                      switch (status) {
                        case 'Paid': return '#10b981'; // Green
                        case 'Pending': return '#f59e0b'; // Orange
                        case 'No Status': return '#6b7280'; // Gray
                        default: return '#3b82f6'; // Blue
                      }
                    })(),
                    fontWeight: 500,
                    fontSize: '14px'
                  }}>
                    {(() => {
                      const monthYear = extractMonthYear(item);
                      return getBillingStatusForMonth(monthYear);
                    })()}
                  </span>
                </div>
                {/* Invoice button for paid items (like Retell AI) */}
                {(() => {
                  const monthYear = extractMonthYear(item);
                  return isPaymentDone(monthYear);
                })() && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const monthYear = extractMonthYear(item);
                      // Get PDF link from database or use default
                      const pdfLink = getPDFLinkForMonth(monthYear);
                      if (pdfLink) {
                        window.open(pdfLink, '_blank');
                      } else {
                        handleDownload(item.title);
                      }
                    }}
                    style={{
                      background: '#fff',
                      border: '1px solid #d1d5db',
                      cursor: 'pointer',
                      color: '#374151',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      marginLeft: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f9fafb';
                      e.target.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.borderColor = '#d1d5db';
                    }}
                    title="View Invoice"
                  >
                    <i className="fas fa-external-link-alt" style={{ fontSize: '11px' }}></i>
                    Invoice
                  </button>
                )}
              </div>
            ) : null}
          </td>
        </tr>
        {/* Children rows */}
        {hasChildren && isExpanded && item.children && item.children.map(child => renderAccordionTableRow(child, level + 1))}
      </React.Fragment>
    );
  };

  // Helper to format duration in mm:ss
  const formatDuration = (durationMs) => {
    if (!durationMs || isNaN(durationMs)) return '-';
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper to group calls by month/year and sum durations
  const getMonthlyDurationSummary = (calls) => {
    const summary = {};
    calls.forEach(call => {
      let dateObj = null;
      if (call.created_at) {
        if (typeof call.created_at === 'string') dateObj = new Date(call.created_at);
        else if (typeof call.created_at === 'number') dateObj = new Date(call.created_at < 1e12 ? call.created_at * 1000 : call.created_at);
      }
      if (!dateObj || isNaN(dateObj.getTime())) return;
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth(); // 0-based
      const day = dateObj.getDate();
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (!summary[key]) {
        summary[key] = {
          totalDuration: 0,
          days: new Set()
        };
      }
      summary[key].totalDuration += call.duration_ms || 0;
      summary[key].days.add(day);
    });
    // Convert to array and sort descending
    return Object.entries(summary)
      .map(([key, data]) => ({
        monthYear: key,
        totalDuration: data.totalDuration,
        days: Array.from(data.days).sort((a, b) => a - b) // Sort days in ascending order
      }))
      .sort((a, b) => b.monthYear.localeCompare(a.monthYear));
  };

  // Create comprehensive monthly summary that includes all billing data sources
  const monthlySummary = useMemo(() => {
    const summary = {};
    
    // Add months from call details
    callDetails.forEach(call => {
      let dateObj = null;
      if (call.created_at) {
        if (typeof call.created_at === 'string') dateObj = new Date(call.created_at);
        else if (typeof call.created_at === 'number') dateObj = new Date(call.created_at < 1e12 ? call.created_at * 1000 : call.created_at);
      }
      if (!dateObj || isNaN(dateObj.getTime())) return;
      
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth(); // 0-based
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (!summary[key]) {
        summary[key] = {
          totalDuration: 0,
          days: new Set(),
          hasCallData: true
        };
      }
      summary[key].totalDuration += call.duration_ms || 0;
      summary[key].days.add(dateObj.getDate());
    });
    
    // Add months from billing status data
    billingStatusByMonth.forEach(item => {
      if (item.month_year) {
        if (!summary[item.month_year]) {
          summary[item.month_year] = {
            totalDuration: 0,
            days: new Set(),
            hasCallData: false
          };
        }
        summary[item.month_year].hasBillingStatus = true;
      }
    });
    
    // Add months from phone number billing
    phoneNumberBilling.forEach(item => {
      if (item.month_year) {
        if (!summary[item.month_year]) {
          summary[item.month_year] = {
            totalDuration: 0,
            days: new Set(),
            hasCallData: false
          };
        }
        summary[item.month_year].hasPhoneBilling = true;
      }
    });
    
    // Add months from Stripe invoices
    stripeInvoices.forEach(invoice => {
      if (invoice.monthYear) {
        if (!summary[invoice.monthYear]) {
          summary[invoice.monthYear] = {
            totalDuration: 0,
            days: new Set(),
            hasCallData: false
          };
        }
        summary[invoice.monthYear].hasStripeInvoice = true;
      }
    });
    
    // Convert to array and sort descending
    return Object.entries(summary)
      .map(([key, data]) => ({
        monthYear: key,
        totalDuration: data.totalDuration,
        days: Array.from(data.days).sort((a, b) => a - b),
        hasCallData: data.hasCallData || false,
        hasBillingStatus: data.hasBillingStatus || false,
        hasPhoneBilling: data.hasPhoneBilling || false,
        hasStripeInvoice: data.hasStripeInvoice || false
      }))
      .sort((a, b) => b.monthYear.localeCompare(a.monthYear));
  }, [callDetails, billingStatusByMonth, phoneNumberBilling, stripeInvoices]);

  // All billing data comes from API - no filtering needed
  const filteredBillingData = billingData;

  // Set the voice engine rate per minute (doubled from Retell pricing)
  const VOICE_ENGINE_RATE_PER_MIN = 0.14; // $0.07 * 2

  // Retell AI Pricing Variables (doubled amounts)
  const RETELL_PRICING = {
    // Voice Engine (doubled from $0.07-0.08)
    voiceEngine: {
      elevenlabs: 0.14, // $0.07 * 2
      openai: 0.16,     // $0.08 * 2
    },
    
    // LLM Pricing (doubled amounts)
    llm: {
      gpt4o: 0.10,           // $0.05 * 2
      gpt4o_mini: 0.012,     // $0.006 * 2
      gpt41: 0.09,           // $0.045 * 2
      gpt41_mini: 0.032,     // $0.016 * 2
      gpt41_nano: 0.008,     // $0.004 * 2
      claude37_sonnet: 0.12, // $0.06 * 2
      claude35_haiku: 0.04,  // $0.02 * 2
      gemini20_flash: 0.012, // $0.006 * 2
      gemini20_flash_lite: 0.006, // $0.003 * 2
    },
    
    // Telephony (doubled from $0.015)
    telephony: {
      retell_twilio: 0.03, // $0.015 * 2
    },
    
    // Add-ons (doubled amounts)
    addons: {
      knowledgeBase: 0.01,    // $0.005 * 2
      batchCall: 0.01,        // $0.005 * 2
      brandedCall: 0.02,      // $0.010 * 2
    },
    
    // Monthly subscriptions (doubled amounts)
    monthly: {
      phoneNumbers: 4.00,     // $2.00 * 2
      sms: 40.00,             // $20.00 * 2
      concurrency: 16.00,     // $8.00 * 2
      knowledgeBase: 16.00,   // $8.00 * 2
      verifiedPhoneNumber: 200.00, // $100.00 * 2
    }
  };

  // Calculate total cost per minute based on components
  const calculateTotalCostPerMinute = (voiceEngine = 'elevenlabs', llm = 'gpt4o', hasKnowledgeBase = false, hasTelephony = true) => {
    let total = RETELL_PRICING.voiceEngine[voiceEngine] || RETELL_PRICING.voiceEngine.elevenlabs;
    total += RETELL_PRICING.llm[llm] || RETELL_PRICING.llm.gpt4o;
    if (hasTelephony) {
      total += RETELL_PRICING.telephony.retell_twilio;
    }
    if (hasKnowledgeBase) {
      total += RETELL_PRICING.addons.knowledgeBase;
    }
    return total;
  };

  // Get the default total cost per minute
  const DEFAULT_COST_PER_MINUTE = calculateTotalCostPerMinute();

  // Helper to extract cost data from call_analysis and call_cost
  const extractCallCosts = (call) => {
    let costs = {
      voice_cost: 0,
      llm_cost: 0,
      kb_cost: 0,
      total_cost: 0,
      duration_seconds: 0
    };
    
    // First try to get costs from the new billing fields
    if (call.total_cost) {
      costs.total_cost = parseFloat(call.total_cost); // Backend already returns dollars
    }
    
    if (call.total_duration_seconds) {
      costs.duration_seconds = parseInt(call.total_duration_seconds);
    }
    
    // Extract detailed costs from call_cost JSON
    if (call.call_cost) {
      try {
        const callCostData = typeof call.call_cost === 'string' ? 
          JSON.parse(call.call_cost) : call.call_cost;
        
        if (callCostData.product_costs && Array.isArray(callCostData.product_costs)) {
          callCostData.product_costs.forEach(product => {
            const cost = parseFloat(product.cost || 0); // Backend already returns dollars
            switch (product.product) {
              case 'Voice Engine':
              case 'elevenlabs_tts':
                costs.voice_cost = cost;
                break;
              case 'LLM':
              case 'gpt_4_1':
              case 'gpt4o':
              case 'gpt4o_mini':
                costs.llm_cost = cost;
                break;
              case 'Knowledge Base Usage':
              case 'knowledge_base_usage':
                costs.kb_cost = cost;
                break;
              case 'Phone Number':
              case 'phone_number':
                costs.phone_cost = cost;
                break;
              case 'Text Testing':
              case 'text_testing':
                costs.text_testing_cost = cost;
                break;
              case 'Short Call Surcharge':
              case 'short_call_surcharge':
                costs.surcharge_cost = cost;
                break;
              case 'LLM Token Surcharge':
              case 'llm_token_surcharge':
                costs.llm_surcharge_cost = cost;
                break;
            }
          });
        }
      } catch (e) {
        console.error('Error parsing call_cost:', e);
      }
    }
    
    // Fallback to old call_analysis if new billing data is not available
    if (costs.total_cost === 0 && call.call_analysis) {
      try {
        const analysis = typeof call.call_analysis === 'string' ? 
          JSON.parse(call.call_analysis) : call.call_analysis;
        
        if (analysis.call_cost) {
          costs.voice_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'elevenlabs_tts')?.cost || 0;
          costs.llm_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'gpt_4_1')?.cost || 0;
          costs.kb_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'knowledge_base_usage')?.cost || 0;
          costs.total_cost = analysis.total_cost || 0;
          costs.duration_seconds = analysis.call_cost.total_duration_seconds || 0;
        }
      } catch (e) {
        console.error('Error parsing call_analysis:', e);
      }
    }
    
    return costs;
  };

  // Add a helper to extract LLM and KB costs per call (legacy function)
  const extractLLMAndKBCosts = (call) => {
    if (!call.call_analysis) return { llm_cost: 0, kb_cost: 0, llm_product: '', kb_product: '' };
    try {
      const analysis = typeof call.call_analysis === 'string' ? JSON.parse(call.call_analysis) : call.call_analysis;
      if (analysis.call_cost && Array.isArray(analysis.call_cost.product_costs)) {
        const llmEntry = analysis.call_cost.product_costs.find(pc => pc.product && pc.product.startsWith('gpt')) || {};
        const kbEntry = analysis.call_cost.product_costs.find(pc => pc.product && pc.product.includes('knowledge_base')) || {};
        return {
          llm_cost: llmEntry.cost || 0,
          kb_cost: kbEntry.cost || 0,
          llm_product: llmEntry.product || '',
          kb_product: kbEntry.product || ''
        };
      }
    } catch (e) {}
    return { llm_cost: 0, kb_cost: 0, llm_product: '', kb_product: '' };
  };

  
  // Unified function to render billing data consistently
  const renderBillingData = (monthYear, monthCalls) => {
    // All data comes from API - no hardcoded data
    
    // Get current month for Component A logic
    const currentDate = new Date();
    const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Avoid flicker: while a refresh is in progress and no API data is
    // available yet, skip rendering child rows (which otherwise show $0.00)
    // and keep only the summary line.
    if (monthYear === currentMonthYear && isRefreshing && !apiBillingData) {
      return { totalCost: Number((totalAmount || 0).toFixed(2)), totalDuration: 0, children: [] };
    }
    
    // Calculate totals from all calls
    let totalCost = 0;
    let totalDuration = 0;
    let totalVoiceEngineCost = 0;
    let totalLLMCost = 0;
    let totalFeaturesCost = 0;
    let totalTextTestingCost = 0;
    let totalConcurrencyCost = 0;
    let totalKbCost = 0;
    let generalCost = 0;
    
    // For current month, use individual service costs from API
    console.log(`🔍 [MONTH CHECK] Processing month: ${monthYear}, Current month: ${currentMonthYear}, Match: ${monthYear === currentMonthYear}`);
    
    if (monthYear === currentMonthYear) {
      // Get individual service costs from API service_breakdown
      const serviceBreakdown = apiBillingData?.service_breakdown || {};
      const metadata = apiBillingData?.metadata || {};
      
      console.log('🔧 [SERVICE BREAKDOWN] Extracting service costs from API data:');
      console.log('🔧 [SERVICE BREAKDOWN] apiBillingData exists:', !!apiBillingData);
      console.log('🔧 [SERVICE BREAKDOWN] service_breakdown exists:', !!serviceBreakdown);
      console.log('🔧 [SERVICE BREAKDOWN] service_breakdown data:', serviceBreakdown);
      console.log('🔧 [SERVICE BREAKDOWN] metadata data:', metadata);
      
      // Debug specific General cost data
      if (serviceBreakdown.j) {
        console.log('🔍 [GENERAL COST] service_breakdown.j details:', {
          raw: serviceBreakdown.j,
          amount: serviceBreakdown.j.amount,
          amountType: typeof serviceBreakdown.j.amount,
          description: serviceBreakdown.j.description
        });
      }
      
      // Try to get from service_breakdown first
      if (serviceBreakdown && Object.keys(serviceBreakdown).length > 0) {
        totalVoiceEngineCost = serviceBreakdown.d?.amount || 0;
        totalLLMCost = serviceBreakdown.e?.amount || 0;
        totalFeaturesCost = serviceBreakdown.f?.amount || 0;
        totalTextTestingCost = serviceBreakdown.g?.amount || 0;
        totalConcurrencyCost = serviceBreakdown.h?.amount || 0;
        totalKbCost = serviceBreakdown.i?.amount || 0;
        generalCost = serviceBreakdown.j?.amount || 0;
        console.log('🔧 [SERVICE BREAKDOWN] Using service_breakdown data from API');
        console.log(`🔍 [GENERAL COST] From service_breakdown.j: $${generalCost.toFixed(2)}`);
        if (generalCost > 0) {
          console.log(`⚠️ [GENERAL COST] WARNING: General cost from API is $${generalCost.toFixed(2)} - this seems unusually high!`);
        }
      } else {
        // Fallback: Calculate from individual call records
        console.log('🔧 [SERVICE BREAKDOWN] service_breakdown not available, calculating from call records');
        const currentMonthCalls = callDetails.filter(call => {
          const callDate = new Date(call.created_at);
          const callMonthYear = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
          return callMonthYear === monthYear;
        });
        
        console.log(`🔧 [SERVICE BREAKDOWN] Found ${currentMonthCalls.length} calls for current month ${monthYear}`);
        
        currentMonthCalls.forEach((call, index) => {
          const voiceCost = parseFloat(call.voice_engine_cost || 0);
          const llmCost = parseFloat(call.llm_cost || 0);
          const featuresCost = parseFloat(call.features_cost || 0);
          const textTestingCost = parseFloat(call.text_testing_cost || 0);
          const concurrencyCost = parseFloat(call.concurrency_cost || 0);
          const kbCost = parseFloat(call.kb_cost || 0);
          const generalCostValue = parseFloat(call.general_cost || 0);
          
          // Debug General cost specifically
          if (generalCostValue > 0) {
            console.log(`🔍 [GENERAL COST] Call ${index + 1}:`, {
              call_id: call.call_id,
              general_cost: call.general_cost,
              parsed_general_cost: generalCostValue,
              created_at: call.created_at
            });
          }
          
          totalVoiceEngineCost += voiceCost;
          totalLLMCost += llmCost;
          totalFeaturesCost += featuresCost;
          totalTextTestingCost += textTestingCost;
          totalConcurrencyCost += concurrencyCost;
          totalKbCost += kbCost;
          generalCost += generalCostValue;
        });
        
        console.log('🔧 [SERVICE BREAKDOWN] Calculated from call records:', {
          voiceEngine: totalVoiceEngineCost,
          llmUsage: totalLLMCost,
          features: totalFeaturesCost,
          textTesting: totalTextTestingCost,
          concurrency: totalConcurrencyCost,
          knowledgeBase: totalKbCost,
          general: generalCost
        });
        
        // Additional debugging for General cost
        console.log(`🔍 [GENERAL COST] Final General cost: $${generalCost.toFixed(2)}`);
        if (generalCost > 0) {
          console.log(`⚠️ [GENERAL COST] WARNING: General cost is $${generalCost.toFixed(2)} - this seems unusually high!`);
        }
      }
      
      // Get total duration from metadata or calculate from calls
      if (metadata && metadata.total_duration_minutes) {
        totalDuration = metadata.total_duration_minutes;
        console.log('🔧 [SERVICE BREAKDOWN] Using duration from metadata:', totalDuration);
      } else {
        // Fallback: Calculate from call records
        const currentMonthCalls = callDetails.filter(call => {
          const callDate = new Date(call.created_at);
          const callMonthYear = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
          return callMonthYear === monthYear;
        });
        
        const totalSeconds = currentMonthCalls.reduce((sum, call) => sum + parseInt(call.total_duration_seconds || 0), 0);
        totalDuration = Math.ceil(totalSeconds / 60);
        console.log('🔧 [SERVICE BREAKDOWN] Calculated duration from calls:', totalDuration, 'minutes');
      }
      
      console.log(`📞 [SERVICE BREAKDOWN] Voice Engine: $${totalVoiceEngineCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] LLM Usage: $${totalLLMCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] Features: $${totalFeaturesCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] Text Testing: $${totalTextTestingCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] Concurrency: $${totalConcurrencyCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] Knowledge Base: $${totalKbCost}`);
      console.log(`📞 [SERVICE BREAKDOWN] General: $${generalCost}`);
      console.log(`⏱️ [SERVICE BREAKDOWN] Total Duration: ${totalDuration} minutes`);
      
      // Calculate total cost: Sum of all individual service costs
      totalCost = totalVoiceEngineCost + totalLLMCost + totalFeaturesCost + 
                  totalTextTestingCost + totalConcurrencyCost + totalKbCost + generalCost;
      
      // Add Component B (Text Testing) cost
      const textTestingData = getTextTestingDataForMonth(monthYear);
      const textTestingCost = Number(textTestingData.cost || 0);
      totalCost += textTestingCost;
      
      // Add Component C (Phone Numbers) cost
      const phoneBillingData = getPhoneNumberBillingForMonth(monthYear);
      const componentCCost = phoneBillingData.cost || 0;
      totalCost += componentCCost;
      
      console.log(`📞 [RENDER] Using individual service costs for current month ${monthYear}:`, {
        voiceEngine: totalVoiceEngineCost,
        llmUsage: totalLLMCost,
        features: totalFeaturesCost,
        concurrency: totalConcurrencyCost,
        knowledgeBase: totalKbCost,
        general: generalCost,
        textTestingCost,
        phoneCost: phoneBillingData.cost,
        componentCCostUsed: componentCCost,
        totalCost
      });
      
    } else {
      // For historical months, process all calls to get accurate totals
    monthCalls.forEach(call => {
      if (call.total_cost) {
        totalCost += parseFloat(call.total_cost); // API already returns dollars, no conversion needed
      }
      
      if (call.total_duration_seconds) {
        totalDuration += parseInt(call.total_duration_seconds);
      }
      
      // Use detailed billing fields (convert cents to dollars)
      if (call.voice_engine_cost) {
        totalVoiceEngineCost += parseFloat(call.voice_engine_cost) / 100;
      }
      
      if (call.llm_cost) {
        totalLLMCost += parseFloat(call.llm_cost) / 100;
      }
      
      if (call.features_cost) {
        totalFeaturesCost += parseFloat(call.features_cost) / 100;
      }
      
      if (call.text_testing_cost) {
        totalTextTestingCost += parseFloat(call.text_testing_cost) / 100;
      }
      
      if (call.concurrency_cost) {
        totalConcurrencyCost += parseFloat(call.concurrency_cost) / 100;
      }
      
      if (call.kb_cost) {
        totalKbCost += parseFloat(call.kb_cost) / 100;
      }
    });
    
    const roundedMinutes = Math.ceil(totalDuration / 60);
    }
    
    // Build children rows from product_costs
    const children = [];
    
    // Get product costs - prioritize consolidated entries over individual calls
    let productCosts = [];
    
    // First, try to find a consolidated entry (has more complete product costs)
    // Prioritize calls with 'october' in the call_id for October 2025
    const consolidatedCall = monthCalls.find(call => 
      call.call_id && call.call_id.toLowerCase().includes('october')
    ) || monthCalls.find(call => 
      call.call_id && (
        call.call_id.toLowerCase().includes('september') ||
        call.call_id.toLowerCase().includes('august') ||
        call.call_id.toLowerCase().includes('summary') ||
        call.call_id.toLowerCase().includes('bill') ||
        call.call_id.toLowerCase().includes('retell-aligned') ||
        (call.total_cost && call.total_cost > 50)
      )
    ) || monthCalls.find(call => call.total_cost && call.total_cost > 50); // Fallback: any call with high cost
    
    // Log the consolidated call being used for October
    if (monthYear === '2025-10' && consolidatedCall) {
      console.log('🔄 MEMO PROCESSING CONSOLIDATED CALL:', {
        call_id: consolidatedCall.call_id,
        total_cost: consolidatedCall.total_cost,
        total_cost_dollars: consolidatedCall.total_cost ? 
          (isNaN(parseFloat(consolidatedCall.total_cost)) ? 'Invalid' : parseFloat(consolidatedCall.total_cost).toFixed(2)) : 'N/A',
        voice_engine_cost: consolidatedCall.voice_engine_cost,
        llm_cost: consolidatedCall.llm_cost,
        kb_cost: consolidatedCall.kb_cost,
        text_testing_cost: consolidatedCall.text_testing_cost,
        short_call_surcharge: consolidatedCall.short_call_surcharge,
        llm_token_surcharge: consolidatedCall.llm_token_surcharge,
        has_call_cost: !!consolidatedCall.call_cost
      });
    }
    
    if (consolidatedCall && consolidatedCall.call_cost) {
      try {
        const callCost = typeof consolidatedCall.call_cost === 'string' ? 
          JSON.parse(consolidatedCall.call_cost) : consolidatedCall.call_cost;
        productCosts = callCost.product_costs || [];
        // Only log occasionally to avoid spam
        if (Math.random() < 0.1) {
          console.log('📊 Using consolidated call product costs:', productCosts.length, 'items');
          console.log('📊 Consolidated call details:', {
            call_id: consolidatedCall.call_id,
            total_cost: consolidatedCall.total_cost,
            product_costs_count: productCosts.length
          });
        }
      } catch (e) {
        console.error('Error parsing consolidated call_cost:', e);
      }
    } else {
      // Always log this for October to see what's happening
      if (monthYear === '2025-10') {
        console.log('📊 No consolidated call found. Available calls:', monthCalls.map(call => ({
          call_id: call.call_id,
          total_cost: call.total_cost,
          has_call_cost: !!call.call_cost
        })));
        console.log('📊 Looking for calls with total_cost > 50:', monthCalls.filter(call => call.total_cost && call.total_cost > 50).length);
        console.log('📊 All calls in callDetails (first 10):', callDetails.slice(0, 10).map(call => ({
          call_id: call.call_id,
          total_cost: call.total_cost,
          has_call_cost: !!call.call_cost,
          created_at: call.created_at
        })));
      }
    }
    
    // Fallback to first call if no consolidated entry found
    if (productCosts.length === 0 && monthCalls.length > 0 && monthCalls[0].call_cost) {
      try {
        const callCost = typeof monthCalls[0].call_cost === 'string' ? 
          JSON.parse(monthCalls[0].call_cost) : monthCalls[0].call_cost;
        productCosts = callCost.product_costs || [];
        // Only log occasionally to avoid spam
        if (Math.random() < 0.1) {
          console.log('📊 Using first call product costs (fallback):', productCosts.length, 'items');
        }
      } catch (e) {
        console.error('Error parsing call_cost:', e);
      }
    }
    
    // If we have product_costs, use them to build detailed children
    if (productCosts.length > 0) {
      // Only log occasionally to avoid spam
    if (Math.random() < 0.1) {
      console.log('📊 Product costs found:', productCosts);
    }
      
      // Deduplicate products by grouping by normalized label and summing costs
      const productMap = new Map();
      
      productCosts.forEach((product) => {
        // Backend returns costs in cents, convert to dollars
        // Consolidated entries need cents-to-dollars conversion
        const cost = parseFloat(product.cost || 0) / 100;
        
        // Safety check to ensure cost is a valid number
        if (isNaN(cost) || !isFinite(cost)) {
          console.warn('⚠️ Invalid cost value:', product.cost, 'for product:', product.product);
          return; // Skip this product
        }
        
        // Get normalized product label
        const normalizedLabel = getProductLabel(product.product);
        
        // Skip Phone Number entries from productCosts - we add them separately from Component C
        // This prevents duplication where Phone Numbers appear in both productCosts and Component C
        const productLower = (product.product || '').toLowerCase();
        if (productLower.includes('phone') || normalizedLabel === 'Phone Numbers' || normalizedLabel === 'Phone Number') {
          console.log(`🔍 [PRODUCT COSTS] Skipping Phone Number entry from productCosts: ${product.product} ($${cost.toFixed(2)}) - will use Component C instead`);
          return; // Skip this product
        }
        
        // Handle different field names from backend
        const usage = product.usage || product.duration || product.quantity || 0;
        const unit = product.unit || (product.duration ? 'minute' : product.quantity ? 'number' : 'event');
        
        // If product already exists, sum the costs and combine usage
        if (productMap.has(normalizedLabel)) {
          const existing = productMap.get(normalizedLabel);
          existing.cost += cost;
          existing.usage += usage;
          // Keep the unit from the first entry (they should be the same)
        } else {
          // Create new entry
          productMap.set(normalizedLabel, {
            label: normalizedLabel,
            cost: cost,
            usage: usage,
            unit: unit
          });
        }
      });
      
      // Convert map to array and create children entries
      let index = 0;
      productMap.forEach((product) => {
        // Format details based on unit type
        let details = '';
        if (product.unit === 'minute' && product.usage > 0) {
          details = `${Math.round(product.usage / 60)} min`; // Convert seconds to minutes
        } else if (product.unit === 'number' && product.usage > 0) {
          details = `${product.usage} numbers`;
        } else if (product.unit === 'event' && product.usage > 0) {
          details = `${product.usage} events`;
        }
        
        children.push({
          id: `${product.label.toLowerCase().replace(/\s+/g, '-')}-${monthYear}-${index}`,
          title: product.label,
          amount: Number(product.cost.toFixed(2)),
          details: details,
          status: ''
        });
        index++;
      });
    } else {
      // For current month, use individual service costs from API
      if (monthYear === currentMonthYear) {
        console.log(`📞 [RENDER] Building children with individual service costs for current month ${monthYear}`);
        console.log(`📞 [RENDER] Service costs:`, {
          voiceEngine: totalVoiceEngineCost,
          llmUsage: totalLLMCost,
          features: totalFeaturesCost,
          concurrency: totalConcurrencyCost,
          knowledgeBase: totalKbCost,
          general: generalCost
        });
        
        // Add individual service rows with actual costs from API
        children.push({
          id: `voice-engine-${monthYear}`,
          title: 'Voice Engine',
          amount: totalVoiceEngineCost,
          details: totalVoiceEngineCost > 0 ? `${totalDuration} minutes` : '',
          status: ''
        });
        
        children.push({
          id: `llm-${monthYear}`,
          title: 'LLM Usage',
          amount: totalLLMCost,
          details: totalLLMCost > 0 ? `${totalDuration} minutes` : '',
          status: ''
        });
        
        children.push({
          id: `features-${monthYear}`,
          title: 'Features',
          amount: totalFeaturesCost,
          details: totalFeaturesCost > 0 ? 'Additional features' : '',
          status: ''
        });
        
        children.push({
          id: `concurrency-${monthYear}`,
          title: 'Concurrency',
          amount: totalConcurrencyCost,
          details: totalConcurrencyCost > 0 ? `${totalDuration} minutes` : '',
          status: ''
        });
        
        children.push({
          id: `kb-${monthYear}`,
          title: 'Knowledge Base',
          amount: totalKbCost,
          details: totalKbCost > 0 ? 'Knowledge base queries' : '',
          status: ''
        });
        
        // Only show General section if there's actual cost data
        console.log(`🔍 [GENERAL] General cost for ${monthYear}: $${generalCost.toFixed(2)}`);
        if (generalCost > 0) {
          console.log(`✅ [GENERAL] Adding General section with cost: $${generalCost.toFixed(2)}`);
          children.push({
            id: `general-${monthYear}`,
            title: 'General',
            amount: generalCost,
            details: `${totalDuration} minutes`,
            status: ''
          });
        } else {
          console.log(`❌ [GENERAL] Skipping General section - no cost data`);
        }
        
        // Add Text Testing from Component B (use monthlyBillingData if available)
        let textTestingCost = 0;
        let textTestingCount = 0;
        if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentB) {
          textTestingCost = Number(monthlyBillingData[monthYear].componentB.amount || 0);
          textTestingCount = monthlyBillingData[monthYear].componentB.count || 0;
        } else {
          const textTestingData = getTextTestingDataForMonth(monthYear);
          textTestingCost = Number(textTestingData.cost || 0);
          textTestingCount = textTestingData.count || 0;
        }
        children.push({
          id: `text-testing-${monthYear}`,
          title: 'Text Testing',
          amount: textTestingCost,
          details: textTestingCost > 0 ? `${textTestingCount} records` : '',
          status: ''
        });
        
        // Add Phone Numbers from Component C (use monthlyBillingData if available)
        let phoneNumbersCost = 0;
        let phoneNumbersCount = 0;
        if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentC) {
          phoneNumbersCost = Number(monthlyBillingData[monthYear].componentC.amount || 0);
          phoneNumbersCount = monthlyBillingData[monthYear].componentC.count || 0;
          console.log(`📱 [RENDER] Using monthlyBillingData for ${monthYear}: Component C = $${phoneNumbersCost.toFixed(2)} (${phoneNumbersCount} records)`);
        } else {
          const phoneBillingData = getPhoneNumberBillingForMonth(monthYear);
          phoneNumbersCost = phoneBillingData.cost || 0;
          phoneNumbersCount = phoneBillingData.count || 0;
          console.log(`📱 [RENDER] Using getPhoneNumberBillingForMonth for ${monthYear}: Component C = $${phoneNumbersCost.toFixed(2)} (${phoneNumbersCount} records)`);
        }
        
        children.push({
          id: `phone-numbers-${monthYear}`,
          title: 'Phone Numbers',
          amount: phoneNumbersCost,
          details: phoneNumbersCost > 0 ? `${phoneNumbersCount} numbers` : '',
          status: ''
        });
        
        console.log(`📞 [RENDER] Created ${children.length} children with individual service costs:`, children.map(c => `${c.title}: $${c.amount}`));
      } else {
        // For historical months, use fallback to aggregated cost fields
      children.push(
        {
          id: `voice-engine-${monthYear}`,
          title: 'Voice Engine',
          amount: Number(totalVoiceEngineCost.toFixed(2)),
            details: totalVoiceEngineCost > 0 ? `${totalDuration} min` : '',
          status: ''
        },
        {
          id: `llm-${monthYear}`,
          title: 'LLM',
          amount: Number(totalLLMCost.toFixed(2)),
            details: totalLLMCost > 0 ? `${totalDuration} min` : '',
          status: ''
        },
        {
          id: `kb-usage-${monthYear}`,
          title: 'Knowledge Base Usage',
          amount: Number(totalKbCost.toFixed(2)),
            details: totalKbCost > 0 ? `${totalDuration} min` : '',
          status: ''
        },
        {
          id: `features-${monthYear}`,
          title: 'Features',
          amount: Number(totalFeaturesCost.toFixed(2)),
            details: totalFeaturesCost > 0 ? 'Additional features' : '',
          status: ''
        },
        {
          id: `text-testing-${monthYear}`,
          title: 'Text Testing',
            amount: (() => {
              // Use monthlyBillingData if available, otherwise fallback to getTextTestingDataForMonth
              if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentB) {
                const cost = Number(monthlyBillingData[monthYear].componentB.amount || 0);
                console.log(`💬 [DISPLAY] Text Testing (Component B) from monthlyBillingData for ${monthYear}: ${cost}`);
                return cost;
              } else {
                const textTestingData = getTextTestingDataForMonth(monthYear);
                console.log(`💬 [DISPLAY] Text Testing data for ${monthYear}:`, textTestingData);
                const cost = Number(textTestingData.cost || 0);
                console.log(`💬 [DISPLAY] Text Testing cost for ${monthYear}: ${cost} (type: ${typeof cost})`);
                return cost;
              }
            })(),
            details: (() => {
              // Use monthlyBillingData if available, otherwise fallback to getTextTestingDataForMonth
              if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentB) {
                const count = monthlyBillingData[monthYear].componentB.count || 0;
                const cost = Number(monthlyBillingData[monthYear].componentB.amount || 0);
                return cost > 0 ? `${count} records` : '';
              } else {
                const textTestingData = getTextTestingDataForMonth(monthYear);
                const cost = Number(textTestingData.cost || 0);
                return cost > 0 ? `${textTestingData.count} records` : '';
              }
            })(),
          status: ''
        },
        {
          id: `phone-numbers-${monthYear}`,
          title: 'Phone Numbers',
          amount: (() => {
            // Use monthlyBillingData if available, otherwise fallback to getPhoneNumberBillingForMonth
            let cost = 0;
            if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentC) {
              cost = Number(monthlyBillingData[monthYear].componentC.amount || 0);
              console.log(`📱 [DISPLAY] Phone Numbers (Component C) from monthlyBillingData for ${monthYear}: ${cost}`);
            } else {
              cost = getPhoneNumberBillingForMonth(monthYear).cost;
            }
            
            
            return cost;
          })(),
            details: (() => {
              // Use monthlyBillingData if available, otherwise fallback to getPhoneNumberBillingForMonth
              if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentC) {
                const count = monthlyBillingData[monthYear].componentC.count || 0;
                const cost = Number(monthlyBillingData[monthYear].componentC.amount || 0);
                return cost > 0 ? `${count} numbers` : '';
              } else {
                const phoneData = getPhoneNumberBillingForMonth(monthYear);
                return phoneData.cost > 0 ? `${phoneData.count} numbers` : '';
              }
            })(),
          status: ''
        }
      );
      }
    }
    
    // Final deduplication: Remove duplicates by title (case-insensitive normalization)
    // This ensures no duplicate entries regardless of which code path was taken
    console.log(`🔍 [DEDUP] Starting deduplication for ${monthYear}. Children count: ${children.length}`);
    console.log(`🔍 [DEDUP] Children titles before dedup:`, children.map(c => c.title));
    
    const normalizedChildrenMap = new Map();
    
    // Comprehensive normalization function - maps all variations to standard titles
    const getStandardTitle = (title) => {
      if (!title) return title;
      const normalized = title.toLowerCase().trim();
      
      // Phone Numbers variations (check first to avoid false matches)
      if (normalized.includes('phone number')) {
        return 'Phone Numbers';
      }
      
      // Knowledge Base variations (check before LLM to avoid false matches)
      if (normalized.includes('knowledge base') || normalized === 'kb' || normalized.startsWith('kb')) {
        return 'Knowledge Base Usage';
      }
      
      // LLM variations (check after knowledge base)
      if (normalized.includes('llm')) {
        return 'LLM';
      }
      
      // Voice Engine variations (check for full phrase first)
      if (normalized.includes('voice engine')) {
        return 'Voice Engine';
      }
      
      // Text Testing variations
      if (normalized.includes('text testing') || normalized.includes('text test')) {
        return 'Text Testing';
      }
      
      // Features variations
      if (normalized.includes('feature') && !normalized.includes('text')) {
        return 'Features';
      }
      
      // Concurrency variations
      if (normalized.includes('concurrency')) {
        return 'Concurrency';
      }
      
      // General variations
      if (normalized.includes('general')) {
        return 'General';
      }
      
      // Default: return original title
      return title;
    };
    
    // Process all children and deduplicate
    children.forEach((child) => {
      if (!child || !child.title) return; // Skip invalid entries
      
      // Get standardized title
      const standardTitle = getStandardTitle(child.title);
      
      // Check if we already have this product
      if (normalizedChildrenMap.has(standardTitle)) {
        // Merge with existing entry: sum costs and combine details
        const existing = normalizedChildrenMap.get(standardTitle);
        
        // For Phone Numbers, don't sum - use the first value (should not be duplicated)
        // Phone Numbers should never be duplicated, so if we see a duplicate, something is wrong
        let combinedAmount;
        if (standardTitle === 'Phone Numbers') {
          console.error(`❌ [DEDUP ERROR] Phone Numbers appears twice for ${monthYear}! This should not happen.`);
          console.error(`❌ [DEDUP ERROR] Existing: $${existing.amount}, New: $${child.amount}`);
          // Use the smaller value
          combinedAmount = Math.min(existing.amount || 0, child.amount || 0);
        } else {
          // Sum the amounts for other products
          combinedAmount = Number((parseFloat(existing.amount || 0) + parseFloat(child.amount || 0)).toFixed(2));
        }
        
        // Combine details intelligently
        const existingDetails = (existing.details || '').trim();
        const newDetails = (child.details || '').trim();
        let combinedDetails = existingDetails;
        
        if (newDetails) {
          // If details are the same, keep one
          if (existingDetails === newDetails) {
            combinedDetails = existingDetails;
          } else if (existingDetails && newDetails) {
            // If both have details, try to extract numbers and sum them (for cases like "22 numbers")
            const existingMatch = existingDetails.match(/(\d+)\s*(numbers?|min|minutes?|sessions?|events?)/i);
            const newMatch = newDetails.match(/(\d+)\s*(numbers?|min|minutes?|sessions?|events?)/i);
            
            if (existingMatch && newMatch && existingMatch[2].toLowerCase() === newMatch[2].toLowerCase()) {
              // Both have the same unit, sum the numbers
              const existingNum = parseInt(existingMatch[1]) || 0;
              const newNum = parseInt(newMatch[1]) || 0;
              const unit = existingMatch[2];
              combinedDetails = `${existingNum + newNum} ${unit}`;
            } else {
              // Different units or formats, prefer the one with more information (non-empty)
              if (newDetails && !existingDetails) {
                combinedDetails = newDetails;
              } else if (existingDetails && !newDetails) {
                combinedDetails = existingDetails;
              } else {
                combinedDetails = newDetails.length > existingDetails.length ? newDetails : existingDetails;
              }
            }
          } else {
            // One has details, the other doesn't - use the one with details
            combinedDetails = newDetails || existingDetails;
          }
        }
        
        // Update the existing entry
        normalizedChildrenMap.set(standardTitle, {
          ...existing,
          amount: combinedAmount,
          details: combinedDetails,
          title: standardTitle
        });
      } else {
        // Create new entry with standardized title
        normalizedChildrenMap.set(standardTitle, {
          ...child,
          title: standardTitle
        });
      }
    });
    
    // Convert map to array - Map already ensures uniqueness by key
    const deduplicatedChildren = Array.from(normalizedChildrenMap.values());
    console.log(`🔍 [DEDUP] After Map deduplication: ${deduplicatedChildren.length} children`);
    console.log(`🔍 [DEDUP] Map children titles:`, deduplicatedChildren.map(c => c.title));
    
    // Final pass: ensure no duplicates remain by using a Set (double-check)
    // Use the same normalization function to ensure consistency
    const seenTitles = new Set();
    const finalChildren = deduplicatedChildren.filter((child) => {
      if (!child || !child.title) return false; // Skip invalid entries
      
      // Use the same normalization function to get the standard title
      const standardTitle = getStandardTitle(child.title);
      const normalizedTitle = standardTitle.toLowerCase().trim();
      
      if (seenTitles.has(normalizedTitle)) {
        console.warn(`⚠️ [DEDUP] Duplicate found and removed: "${child.title}" (normalized: "${standardTitle}")`);
        return false; // Skip duplicate
      }
      seenTitles.add(normalizedTitle);
      return true; // Keep unique
    });
    
    // Debug: Log final children to verify deduplication
    console.log(`✅ [DEDUP] Final deduplication complete for ${monthYear}`);
    console.log(`📊 [DEDUP] Original count: ${children.length}, After Map: ${deduplicatedChildren.length}, Final: ${finalChildren.length}`);
    console.log(`📊 [DEDUP] Final children titles:`, finalChildren.map(c => `${c.title} ($${c.amount})`));
    
    if (finalChildren.length !== children.length) {
      console.log(`✅ [DEDUP] Removed ${children.length - finalChildren.length} duplicates`);
    }
    
    return {
      totalCost: Number(totalCost.toFixed(2)),
      totalDuration: totalDuration,
      children: finalChildren
    };
  };  // Helper to get phone number billing data for a specific month (memoized)
  // IMPORTANT: This function should ONLY use monthlyBillingData which contains the correct Component C amount from the API
  // DO NOT use apiPhoneNumbersData as it contains ALL phone numbers and summing them would give incorrect results
  const getPhoneNumberBillingForMonth = useCallback((monthYear) => {
    // FIRST PRIORITY: Use monthlyBillingData if available (most accurate, fetched per month with correct Component C amount)
    // This is the ONLY reliable source as it uses calculation.component_c.amount from the API ($42)
    if (monthlyBillingData[monthYear] && monthlyBillingData[monthYear].componentC) {
      const componentC = monthlyBillingData[monthYear].componentC;
      const count = componentC.count || 0;
      const cost = Number(componentC.amount || 0);
      console.log(`📱 [MONTHLY BILLING DATA] Using Component C from monthlyBillingData for ${monthYear}: ${count} numbers, $${cost.toFixed(2)}`);
      return { count, cost };
    }
    
    // REMOVED: Do NOT use apiPhoneNumbersData as fallback
    // apiPhoneNumbersData contains ALL phone numbers from all months, summing them would give $84 (double the correct amount)
    // The API returns the correct Component C amount ($42) in calculation.component_c.amount
    // We should only use monthlyBillingData which is populated from fetchAllMonthlyBillingData
    
    // Second try: Use old phone number billing data for historical months (only if monthlyBillingData is not available)
    let phoneData = phoneNumberBilling.find(item => item.month_year === monthYear);
    
    // If no exact match and we're looking for current month, try to find any data with null month_year
    if (!phoneData && monthYear) {
      const currentDate = new Date();
      const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (monthYear === currentMonthYear) {
        phoneData = phoneNumberBilling.find(item => item.month_year === null || item.month_year === undefined);
      }
    }
    
    if (phoneData) {
      console.log(`📱 [FALLBACK] Using old phone billing data for ${monthYear}: ${phoneData.phone_count} numbers, $${phoneData.total_cost}`);
      return {
        count: phoneData.phone_count || 0,
        cost: parseFloat(phoneData.total_cost || 0)
      };
    }
    
    // If monthlyBillingData is not available, log a warning
    console.warn(`⚠️ [PHONE BILLING] monthlyBillingData not available for ${monthYear}. Please ensure fetchAllMonthlyBillingData is called.`);
    return { count: 0, cost: 0 };
  }, [phoneNumberBilling, monthlyBillingData]);

  // Helper to get Text Testing data from API Component B (Chat Sessions) for a specific month (memoized)
  // Function to get exact Component B amount from server
  const getExactComponentBAmount = useCallback(async () => {
    try {
      const currentUserId = getUserId();
      if (!currentUserId) {
        console.error('💬 [EXACT] No user ID available');
        return null;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/billing/calculate/${currentUserId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.calculation && data.calculation.component_b) {
          const exactAmount = data.calculation.component_b.amount;
          console.log(`💬 [EXACT] Server Component B amount:`, exactAmount);
          return exactAmount;
        }
      }
    } catch (error) {
      console.error('💬 [EXACT] Error fetching exact Component B amount:', error);
    }
    return null;
  }, []);

  const getTextTestingDataForMonth = useCallback((monthYear) => {
    const currentDate = new Date();
    const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    console.log(`💬 [DEBUG] Getting Text Testing data for ${monthYear}, current month: ${currentMonthYear}`);
    console.log(`💬 [DEBUG] apiChatSessionsData available:`, !!apiChatSessionsData);
    console.log(`💬 [DEBUG] apiBillingData available:`, !!apiBillingData);
    
    // First try: Get Component B amount directly from API calculation (works for all months)
    if (apiBillingData && apiBillingData.calculation && apiBillingData.calculation.component_b) {
      const componentBAmount = apiBillingData.calculation.component_b.amount || 0;
      const componentBRecords = apiBillingData.calculation.component_b.records || [];
      const count = componentBRecords.length || apiChatSessionsData?.length || 0;
      const cost = typeof componentBAmount === 'number' ? componentBAmount : parseFloat(componentBAmount || 0);
      
      console.log(`💬 [API] Using Component B amount from API calculation for ${monthYear}: ${count} sessions, $${cost.toFixed(2)}`);
      return { count, cost };
    }
    
    // For current month, use API Component B data
    if (monthYear === currentMonthYear) {
      // First try to get from the exact Component B amount (most accurate)
      if (exactComponentBAmount !== null) {
        console.log(`💬 [EXACT] Using exact Component B amount:`, exactComponentBAmount);
        const totalCost = typeof exactComponentBAmount === 'number' ? exactComponentBAmount : parseFloat(exactComponentBAmount);
        const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
        console.log(`💬 [API] Using exact Component B amount for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        console.log(`💬 [DEBUG] Exact amount value:`, exactComponentBAmount);
        console.log(`💬 [DEBUG] Exact amount type:`, typeof exactComponentBAmount);
        console.log(`💬 [DEBUG] Using exact server value:`, totalCost);
        console.log(`💬 [DEBUG] TotalCost toFixed(4):`, totalCost.toFixed(4));
        console.log(`💬 [DEBUG] TotalCost toFixed(6):`, totalCost.toFixed(6));
        return { count, cost: totalCost };
      }
      
      // Second try to get from the raw API calculation component_b
      if (apiBillingData && apiBillingData.calculation && apiBillingData.calculation.component_b) {
        console.log(`💬 [DEBUG] API calculation component_b:`, apiBillingData.calculation.component_b);
        const rawAmount = apiBillingData.calculation.component_b.amount || 0;
        // Use the exact server value without any additional parsing that might cause rounding
        const totalCost = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount);
        const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
        console.log(`💬 [API] Using Component B calculation data for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        console.log(`💬 [DEBUG] Raw component_b.amount value:`, rawAmount);
        console.log(`💬 [DEBUG] Raw amount type:`, typeof rawAmount);
        console.log(`💬 [DEBUG] Using exact server value:`, totalCost);
        console.log(`💬 [DEBUG] TotalCost toFixed(4):`, totalCost.toFixed(4));
        console.log(`💬 [DEBUG] TotalCost toFixed(6):`, totalCost.toFixed(6));
        return { count, cost: totalCost };
      }
      
      // Second try to get from stored Component B amount
      if (apiBillingData && apiBillingData.summary && apiBillingData.summary.component_b_amount !== undefined) {
        console.log(`💬 [DEBUG] API summary data:`, apiBillingData.summary);
        const rawAmount = apiBillingData.summary.component_b_amount || 0;
        // Use the exact server value without any additional parsing that might cause rounding
        const totalCost = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount);
        
        const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
        console.log(`💬 [API] Using stored Component B amount for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        console.log(`💬 [DEBUG] Raw component_b_amount value:`, rawAmount);
        console.log(`💬 [DEBUG] Raw amount type:`, typeof rawAmount);
        console.log(`💬 [DEBUG] Using exact server value:`, totalCost);
        console.log(`💬 [DEBUG] TotalCost toFixed(4):`, totalCost.toFixed(4));
        console.log(`💬 [DEBUG] TotalCost toFixed(6):`, totalCost.toFixed(6));
        return { count, cost: totalCost };
      }
      
      // Try to get from API summary chat_sessions data
      if (apiBillingData && apiBillingData.summary) {
        console.log(`💬 [DEBUG] API summary data:`, apiBillingData.summary);
        const rawAmount = apiBillingData.summary.chat_sessions || 0;
        // Use the exact server value without any additional parsing that might cause rounding
        const totalCost = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount);
        const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
        console.log(`💬 [API] Using Component B summary data for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        console.log(`💬 [DEBUG] Raw chat_sessions value:`, rawAmount);
        console.log(`💬 [DEBUG] Raw amount type:`, typeof rawAmount);
        console.log(`💬 [DEBUG] Using exact server value:`, totalCost);
        console.log(`💬 [DEBUG] TotalCost toFixed(4):`, totalCost.toFixed(4));
        console.log(`💬 [DEBUG] TotalCost toFixed(6):`, totalCost.toFixed(6));
        return { count, cost: totalCost };
      }
      
      
      // Try individual records as last resort
      if (apiChatSessionsData && apiChatSessionsData.length > 0) {
        console.log(`💬 [DEBUG] API Component B data structure:`, apiChatSessionsData);
        console.log(`💬 [DEBUG] First chat session:`, apiChatSessionsData[0]);
        
        // Try different possible field names for the cost
        const totalCost = apiChatSessionsData.reduce((sum, chat) => {
          const cost = parseFloat(chat.chat_sessions_price || chat.amount || chat.cost || chat.price || 0);
          console.log(`💬 [DEBUG] Chat session cost:`, { chat, cost });
          return sum + cost;
        }, 0);
        
        const count = apiChatSessionsData.length;
        console.log(`💬 [API] Using Component B individual records for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        return { count, cost: totalCost };
      }
      
      // Try to get from the raw API response if available
      if (apiBillingData && apiBillingData.calculation) {
        console.log(`💬 [DEBUG] Raw API calculation:`, apiBillingData.calculation);
        const rawAmount = apiBillingData.calculation.component_b?.amount || 0;
        // Use the exact server value without any additional parsing that might cause rounding
        const totalCost = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount);
        const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
        console.log(`💬 [API] Using raw Component B amount for Text Testing current month ${monthYear}: ${count} sessions, $${totalCost.toFixed(2)}`);
        console.log(`💬 [DEBUG] Raw calculation component_b.amount value:`, rawAmount);
        console.log(`💬 [DEBUG] Raw amount type:`, typeof rawAmount);
        console.log(`💬 [DEBUG] Using exact server value:`, totalCost);
        console.log(`💬 [DEBUG] TotalCost toFixed(4):`, totalCost.toFixed(4));
        console.log(`💬 [DEBUG] TotalCost toFixed(6):`, totalCost.toFixed(6));
        return { count, cost: totalCost };
      }
      
      // Final fallback to 0
      console.log(`💬 [FALLBACK] No API Component B data available for current month ${monthYear}, using 0`);
      return { count: 0, cost: 0 };
    }
    
    // For historical months, try to get from API summary if available
    if (apiBillingData && apiBillingData.summary) {
      const chatSessionsAmount = apiBillingData.summary.chat_sessions || 0;
      const count = apiChatSessionsData ? apiChatSessionsData.length : 0;
      const cost = typeof chatSessionsAmount === 'number' ? chatSessionsAmount : parseFloat(chatSessionsAmount || 0);
      
      if (cost > 0) {
        console.log(`💬 [API] Using Component B from summary for historical month ${monthYear}: ${count} sessions, $${cost.toFixed(2)}`);
        return { count, cost };
      }
    }
    
    // For historical months, return 0 (no API data available)
    console.log(`💬 [FALLBACK] No API Component B data for historical month ${monthYear}, using 0`);
    return { count: 0, cost: 0 };
  }, [apiChatSessionsData, apiBillingData, exactComponentBAmount]);

  // Helper to get total cost from monthly totals for a specific month (memoized)
  // This includes costs from call_logs, ai_assistant_call_logs, and phone_numbers
  const getMonthlyTotalCost = useCallback((monthYear) => {
    // Use the new API data instead of old monthlyTotals
    // The new API already includes calls + chats + phone numbers
    
    // For now, return 0 and let the main billing data handle the display
    // This prevents double counting and ensures we use the correct API
    return 0;
  }, []);

  // Memoize the expensive billing data calculations
  const billingDataWithMonthly = useMemo(() => {
    // Ensure current month is always included if no data exists
    const currentDate = new Date();
    const currentMonthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Add current month if it doesn't exist in monthlySummary
    if (monthlySummary.length === 0 || !monthlySummary.some(m => m.monthYear === currentMonthYear)) {
      monthlySummary.push({
        monthYear: currentMonthYear,
        totalDuration: 0,
        days: [],
        hasCallData: false,
        hasBillingStatus: false,
        hasPhoneBilling: false,
        hasStripeInvoice: false
      });
    }
  
  // Sort the monthly summary to ensure proper order
  monthlySummary.sort((a, b) => b.monthYear.localeCompare(a.monthYear));
    // Include forceRefresh to trigger recalculation when manually updated
    const _ = forceRefresh;
    console.log('🔄 billingDataWithMonthly memo recalculating, forceRefresh:', forceRefresh);
    console.log('🔍 DEBUG - callDetails length:', callDetails.length);
    console.log('🔍 DEBUG - filteredBillingData length:', filteredBillingData.length);
    console.log('🔍 DEBUG - monthlySummary length:', monthlySummary.length);
    console.log('🔍 DEBUG - monthlySummary data:', monthlySummary.map(m => ({
      monthYear: m.monthYear,
      hasCallData: m.hasCallData,
      hasBillingStatus: m.hasBillingStatus,
      hasPhoneBilling: m.hasPhoneBilling,
      hasStripeInvoice: m.hasStripeInvoice
    })));
    console.log('🔍 DEBUG - Current month should be:', currentMonthYear);
    console.log('🔍 DEBUG - API billing data available:', !!apiBillingData);
    console.log('🔍 DEBUG - API total_cost_dollars:', apiBillingData?.total_cost_dollars);
    console.log('🔍 DEBUG - API Component C (Phone Numbers) data available:', !!apiPhoneNumbersData);
    console.log('🔍 DEBUG - API Component C records count:', apiPhoneNumbersData?.length || 0);
    console.log('🔍 DEBUG - API Component B (Chat Sessions) data available:', !!apiChatSessionsData);
    console.log('🔍 DEBUG - API Component B records count:', apiChatSessionsData?.length || 0);
    console.log('🔍 DEBUG - billingStatusByMonth length:', billingStatusByMonth.length);
    console.log('🔍 DEBUG - phoneNumberBilling length:', phoneNumberBilling.length);
    console.log('🔍 DEBUG - stripeInvoices length:', stripeInvoices.length);
    return [
      ...filteredBillingData,
      ...monthlySummary.map((data) => {
        const monthCalls = callDetails.filter(call => {
          const callDate = new Date(call.created_at);
          const callMonth = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
          const matches = callMonth === data.monthYear;
          
          // Special handling for consolidated entries - they should always be included for their target month
          if (call.call_id && call.call_id.toLowerCase().includes('october') && data.monthYear === '2025-10') {
            console.log('🔍 OCTOBER CONSOLIDATED ENTRY FOUND:', {
              call_id: call.call_id,
              created_at: call.created_at,
              callDate: callDate,
              callMonth: callMonth,
              targetMonth: data.monthYear,
              matches: matches,
              willInclude: true
            });
            return true; // Always include October consolidated entries
          }
          
          // Debug logging for October 2025 (log all calls to see what's happening)
          if (data.monthYear === '2025-10') {
            console.log('🔍 Date filtering debug:', {
              call_id: call.call_id,
              created_at: call.created_at,
              callDate: callDate,
              callMonth: callMonth,
              targetMonth: data.monthYear,
              matches: matches
            });
          }
          
          return matches;
        });
        
        // If no call data but we have other billing data for this month, create a placeholder
        if (monthCalls.length === 0 && (data.hasBillingStatus || data.hasPhoneBilling || data.hasStripeInvoice)) {
          console.log(`📊 No call data for ${data.monthYear} but has other billing data, creating placeholder`);
        }
        
        // Special handling for consolidated entries (like october-2025-retell-aligned)
        const consolidatedEntry = callDetails.find(call => 
          call.call_id && (
            call.call_id.toLowerCase().includes(data.monthYear.toLowerCase().replace('-', '')) ||
            call.call_id.toLowerCase().includes(data.monthYear.toLowerCase()) ||
            (data.monthYear === '2025-10' && call.call_id.toLowerCase().includes('october'))
          )
        );
        
        // Debug logging for October 2025 (always log to see what's happening)
        if (data.monthYear === '2025-10') {
          console.log('🔍 Consolidated entry debug:', {
            foundConsolidatedEntry: !!consolidatedEntry,
            consolidatedEntryCallId: consolidatedEntry?.call_id,
            consolidatedEntryTotalCost: consolidatedEntry?.total_cost,
            consolidatedEntryHasCallCost: !!consolidatedEntry?.call_cost,
            totalCallsInMonth: monthCalls.length,
            allCallIds: callDetails.map(call => call.call_id).slice(0, 10), // First 10 call IDs
            octoberCalls: callDetails.filter(call => 
              call.call_id && call.call_id.toLowerCase().includes('october')
            ).map(call => ({
              call_id: call.call_id,
              total_cost: call.total_cost,
              has_call_cost: !!call.call_cost
            })),
            highCostCalls: callDetails.filter(call => 
              call.total_cost && call.total_cost > 50
            ).map(call => ({
              call_id: call.call_id,
              total_cost: call.total_cost,
              has_call_cost: !!call.call_cost
            }))
          });
        }
        
        if (consolidatedEntry && monthCalls.length === 0) {
          monthCalls.push(consolidatedEntry);
        }
        
        // Use detailed billing data from API
        let totalCost = 0;
        let totalDuration = 0;
        let totalVoiceEngineCost = 0;
        let totalLLMCost = 0;
        let totalFeaturesCost = 0;
        let totalTextTestingCost = 0;
        let totalConcurrencyCost = 0;
        let totalKbCost = 0;
        
        monthCalls.forEach(call => {
          // Don't use the inflated total_cost from API - calculate from individual services instead
          
          if (call.total_duration_seconds) {
            totalDuration += parseInt(call.total_duration_seconds);
          }
          
          // Use detailed billing fields (convert cents to dollars)
          if (call.voice_engine_cost) {
            totalVoiceEngineCost += parseFloat(call.voice_engine_cost) / 100;
          }
          
          if (call.llm_cost) {
            totalLLMCost += parseFloat(call.llm_cost) / 100;
          }
          
          if (call.features_cost) {
            totalFeaturesCost += parseFloat(call.features_cost) / 100;
          }
          
          if (call.text_testing_cost) {
            totalTextTestingCost += parseFloat(call.text_testing_cost) / 100;
          }
          
          if (call.concurrency_cost) {
            totalConcurrencyCost += parseFloat(call.concurrency_cost) / 100;
          }
          
          if (call.kb_cost) {
            totalKbCost += parseFloat(call.kb_cost) / 100;
          }
        });
        
        const roundedMinutes = Math.ceil(totalDuration / 60);
        
        // Use unified rendering function
        const billingData = renderBillingData(data.monthYear, monthCalls);
        const children = billingData.children;
        let billingTotalCost = billingData.totalCost;
        const billingTotalDuration = billingData.totalDuration;
        
        // CRITICAL FIX: Use API's total_cost directly instead of recalculated total
        // The API returns the correct total ($64.57), but renderBillingData might calculate it incorrectly
        // Always use the API's total_cost if available, as it's the source of truth
        if (monthlyBillingData[data.monthYear] && monthlyBillingData[data.monthYear].totalCost) {
          const apiTotalCost = monthlyBillingData[data.monthYear].totalCost;
          if (Math.abs(apiTotalCost - billingTotalCost) > 0.01) {
            console.log(`✅ [FIX] Using API total_cost for ${data.monthYear}: $${apiTotalCost.toFixed(2)} (instead of calculated $${billingTotalCost.toFixed(2)})`);
          }
          billingTotalCost = apiTotalCost;
        }
        
        // Get phone number billing cost for this month (now uses API Component C data for current month)
        const phoneBillingData = getPhoneNumberBillingForMonth(data.monthYear);
        const phoneNumbersCost = phoneBillingData.cost;
        
        // Debug logging for phone number billing
        if (data.monthYear === currentMonthYear) {
          console.log(`📱 [DEBUG] Phone billing for current month ${data.monthYear}:`, {
            usingApiData: !!apiPhoneNumbersData,
            phoneCount: phoneBillingData.count,
            phoneCost: phoneBillingData.cost,
            apiRecordsCount: apiPhoneNumbersData?.length || 0
          });
        }
        
        // If no call data but we have other billing data, create basic children
        if (monthCalls.length === 0 && (data.hasBillingStatus || data.hasPhoneBilling || data.hasStripeInvoice)) {
          console.log(`📊 Creating billing entry for ${data.monthYear} with non-call data`);
          
          // Add phone number billing if available (using API Component C data for current month)
          if (data.hasPhoneBilling && phoneNumbersCost > 0) {
            children.push({
              id: `phone-numbers-${data.monthYear}`,
              title: 'Phone Numbers',
              amount: phoneNumbersCost,
              details: `${phoneBillingData.count} numbers`,
              status: ''
            });
          }
          
          // Add placeholder for other services if we have billing status
          if (data.hasBillingStatus) {
            children.push(
              {
                id: `voice-engine-${data.monthYear}`,
                title: 'Voice Engine',
                amount: 0.00,
                details: '0 min',
                status: ''
              },
              {
                id: `llm-${data.monthYear}`,
                title: 'LLM',
                amount: 0.00,
                details: '0 min',
                status: ''
              },
              {
                id: `kb-usage-${data.monthYear}`,
                title: 'Knowledge Base Usage',
                amount: 0.00,
                details: '0 min',
                status: ''
              }
            );
          }
        }
        
        // Apply deduplication to children array after all items are added
        // This ensures no duplicates even if items are added from multiple places
        const deduplicateChildren = (childrenArray) => {
          const normalizedMap = new Map();
          
          const getStandardTitle = (title) => {
            if (!title) return title;
            const normalized = title.toLowerCase().trim();
            
            if (normalized.includes('phone number')) return 'Phone Numbers';
            if (normalized.includes('knowledge base') || normalized === 'kb' || normalized.startsWith('kb')) return 'Knowledge Base Usage';
            if (normalized.includes('llm')) return 'LLM';
            if (normalized.includes('voice engine')) return 'Voice Engine';
            if (normalized.includes('text testing') || normalized.includes('text test')) return 'Text Testing';
            if (normalized.includes('feature') && !normalized.includes('text')) return 'Features';
            if (normalized.includes('concurrency')) return 'Concurrency';
            if (normalized.includes('general')) return 'General';
            
            return title;
          };
          
          childrenArray.forEach((child) => {
            if (!child || !child.title) return;
            
            const standardTitle = getStandardTitle(child.title);
            
            if (normalizedMap.has(standardTitle)) {
              const existing = normalizedMap.get(standardTitle);
              const combinedAmount = Number((parseFloat(existing.amount || 0) + parseFloat(child.amount || 0)).toFixed(2));
              
              const existingDetails = (existing.details || '').trim();
              const newDetails = (child.details || '').trim();
              let combinedDetails = existingDetails;
              
              if (newDetails) {
                if (existingDetails === newDetails) {
                  combinedDetails = existingDetails;
                } else if (existingDetails && newDetails) {
                  const existingMatch = existingDetails.match(/(\d+)\s*(numbers?|min|minutes?|sessions?|events?)/i);
                  const newMatch = newDetails.match(/(\d+)\s*(numbers?|min|minutes?|sessions?|events?)/i);
                  
                  if (existingMatch && newMatch && existingMatch[2].toLowerCase() === newMatch[2].toLowerCase()) {
                    const existingNum = parseInt(existingMatch[1]) || 0;
                    const newNum = parseInt(newMatch[1]) || 0;
                    const unit = existingMatch[2];
                    combinedDetails = `${existingNum + newNum} ${unit}`;
                  } else {
                    combinedDetails = newDetails.length > existingDetails.length ? newDetails : existingDetails;
                  }
                } else {
                  combinedDetails = newDetails || existingDetails;
                }
              }
              
              normalizedMap.set(standardTitle, {
                ...existing,
                amount: combinedAmount,
                details: combinedDetails,
                title: standardTitle
              });
            } else {
              normalizedMap.set(standardTitle, {
                ...child,
                title: standardTitle
              });
            }
          });
          
          return Array.from(normalizedMap.values());
        };
        
        // Deduplicate children after all items are added
        const deduplicatedChildren = deduplicateChildren(children);
        if (deduplicatedChildren.length !== children.length) {
          console.log(`✅ [DEDUP] Removed ${children.length - deduplicatedChildren.length} duplicates after additional items added`);
        }
        // Replace children with deduplicated version
        children.length = 0;
        children.push(...deduplicatedChildren);
        
        // Use the combined total cost from all tables (call_logs, ai_assistant_call_logs, phone_numbers)
        totalCost = getMonthlyTotalCost(data.monthYear);
        
        // Only log occasionally to avoid spam
        if (Math.random() < 0.1) {
          console.log('🔍 DEBUGGING MAIN TOTAL CALCULATION:', {
            monthYear: data.monthYear,
            billingTotalCost: billingTotalCost,
            phoneNumbersCost: phoneNumbersCost,
            calculatedTotalCost: totalCost,
            individualServicesSum: children.reduce((sum, child) => sum + child.amount, 0)
          });
        }
        
        // Find consolidated call for this month (same logic as in renderBillingData)
        const consolidatedCall = monthCalls.find(call => 
          call.call_id && call.call_id.toLowerCase().includes('october')
        ) || monthCalls.find(call => 
          call.call_id && (
            call.call_id.toLowerCase().includes('september') ||
            call.call_id.toLowerCase().includes('august') ||
            call.call_id.toLowerCase().includes('summary') ||
            call.call_id.toLowerCase().includes('bill') ||
            call.call_id.toLowerCase().includes('retell-aligned') ||
            (call.total_cost && call.total_cost > 50)
          )
        ) || monthCalls.find(call => call.total_cost && call.total_cost > 50); // Fallback: any call with high cost
        
        // Calculate summary amount based on available data
        let summaryAmount = 0;
        
        // For current month, use totalAmount from API (includes A + B + C calculation)
        console.log(`🔍 Month comparison: ${data.monthYear} === ${currentMonthYear} ? ${data.monthYear === currentMonthYear}`);
        console.log(`🔍 DEBUG - Current month data:`, {
          monthYear: data.monthYear,
          currentMonthYear: currentMonthYear,
          isCurrentMonth: data.monthYear === currentMonthYear,
          apiBillingDataExists: !!apiBillingData,
          apiTotalCostDollars: apiBillingData?.total_cost_dollars,
          totalAmount: totalAmount,
          monthCallsLength: monthCalls.length,
          billingTotalCost: billingTotalCost
        });
        
        // CRITICAL FIX: Always use API's total_cost from monthlyBillingData if available
        // This is the source of truth and prevents incorrect calculations
        if (monthlyBillingData[data.monthYear] && monthlyBillingData[data.monthYear].totalCost) {
          summaryAmount = monthlyBillingData[data.monthYear].totalCost;
          console.log(`✅ [FIX] Using API total_cost from monthlyBillingData for ${data.monthYear}: $${summaryAmount.toFixed(2)}`);
        } else if (data.monthYear === currentMonthYear) {
          // Use the API billing data for current month (includes all components A + B + C)
          if (apiBillingData && apiBillingData.total_cost_dollars) {
            summaryAmount = apiBillingData.total_cost_dollars;
            console.log(`💰 Using API total_cost_dollars for current month ${data.monthYear}: $${summaryAmount}`);
            console.log(`💰 API total_cost_dollars type: ${typeof apiBillingData.total_cost_dollars}`);
            console.log(`💰 API total_cost_dollars value: ${apiBillingData.total_cost_dollars}`);
            console.log(`💰 API Component A (Voice): $${apiBillingData.summary?.voice_calls || 0}`);
            console.log(`💰 API Component B (Chat): $${apiBillingData.summary?.chat_sessions || 0}`);
            console.log(`💰 API Component C (Phone): $${apiBillingData.summary?.phone_numbers || 0}`);
            console.log(`💰 API Calculation: ${apiBillingData.summary?.voice_calls || 0} + ${apiBillingData.summary?.chat_sessions || 0} + ${apiBillingData.summary?.phone_numbers || 0} = ${summaryAmount}`);
          } else if (totalAmount && totalAmount > 0) {
            summaryAmount = totalAmount;
            console.log(`💰 Using totalAmount fallback for current month ${data.monthYear}: $${summaryAmount}`);
            console.log(`💰 totalAmount type: ${typeof totalAmount}`);
          } else if (children.length > 0) {
            // Fallback to children sum if API data and totalAmount are not available
            summaryAmount = children.reduce((sum, child) => sum + child.amount, 0);
            console.log(`💰 Using children sum fallback for current month ${data.monthYear}: $${summaryAmount}`);
            console.log(`💰 Children amounts:`, children.map(c => ({ title: c.title, amount: c.amount })));
            console.log(`💰 Children breakdown:`, children.map(c => `${c.title}: $${c.amount}`).join(', '));
          } else {
            summaryAmount = 0;
            console.log(`💰 No data available for current month ${data.monthYear}, using 0`);
          }
          console.log(`💰 API data available: ${!!apiBillingData}`);
          console.log(`💰 API total_cost_dollars: ${apiBillingData?.total_cost_dollars || 'N/A'}`);
          console.log(`💰 totalAmount: ${totalAmount}`);
          console.log(`💰 children count: ${children.length}`);
          console.log(`💰 FINAL summaryAmount: ${summaryAmount}`);
        } else if (monthCalls.length > 0) {
          // For historical months, use the billing total cost from individual calls
          summaryAmount = billingTotalCost;
          console.log(`💰 Using billingTotalCost for historical month ${data.monthYear}: $${summaryAmount}`);
        } else if (data.hasPhoneBilling || data.hasBillingStatus || data.hasStripeInvoice) {
          // If no call data but we have other billing data, calculate from children
          summaryAmount = children.reduce((sum, child) => sum + child.amount, 0);
          console.log(`💰 Using children sum for month ${data.monthYear}: $${summaryAmount}`);
        } else {
          // Fallback to 0 for months with no data
          summaryAmount = 0;
          console.log(`💰 No data for month ${data.monthYear}, using 0`);
        }
        
        // Only log occasionally to avoid spam
        if (Math.random() < 0.1) {
          console.log('🔍 FINAL SUMMARY AMOUNT:', {
            monthYear: data.monthYear,
            summaryAmount: summaryAmount,
            individualServicesSum: children.reduce((sum, child) => sum + child.amount, 0),
            totalCost: totalCost,
            phoneNumbersCost: phoneNumbersCost,
            childrenCount: children.length,
            hasCallData: data.hasCallData,
            hasPhoneBilling: data.hasPhoneBilling,
            hasBillingStatus: data.hasBillingStatus
          });
        }
        
        console.log(`📞 [BILLING DATA] Final data for ${data.monthYear}:`, {
          hasChildren: children.length > 0,
          childrenCount: children.length,
          children: children.map(c => `${c.title}: $${c.amount}`),
          summaryAmount
        });
        
        return {
          id: `summary-${data.monthYear}`,
          title: formatMonthYear(data.monthYear, 6),
          monthYear: data.monthYear, // Add the monthYear property
          amount: summaryAmount,
          details: '',
          status: '',
          type: 'summary',
          hasChildren: children.length > 0,
          children
        };
      })
    ];
  }, [filteredBillingData, monthlySummary, callDetails, getPhoneNumberBillingForMonth, getTextTestingDataForMonth, getMonthlyTotalCost, forceRefresh, totalAmount, apiBillingData, apiPhoneNumbersData, apiChatSessionsData]);


  // Helper to extract monthYear from item
  const extractMonthYear = (item) => {
    let monthYear = item.monthYear;
    if (!monthYear && item.title) {
      // Try to extract YYYY-MM format from title
      const monthYearMatch = item.title.match(/(\d{4}-\d{2})/);
      if (monthYearMatch) {
        monthYear = monthYearMatch[1];
      }
    }
    return monthYear || item.title;
  };

  // Helper to determine billing status for a month (memoized)
  const getBillingStatusForMonth = useCallback((monthYear) => {
    // First check if there's a Stripe invoice for this month
    const stripeInvoice = stripeInvoices.find(invoice => invoice.monthYear === monthYear);
    if (stripeInvoice) {
      // If there's a Stripe invoice, check its status
      if (stripeInvoice.status === 'paid' || stripeInvoice.status === 'open') {
        return 'Paid';
      }
    }
    
    // Get billing status from server API data - simplified for single status per month
    const billingStatusData = billingStatusByMonth.find(item => item.month_year === monthYear);
    if (billingStatusData && billingStatusData.billing_status) {
      return billingStatusData.billing_status;
    }
    
    // Fallback to database logic if server data not available
    const monthCalls = callDetails.filter(call => {
      const callDate = new Date(call.created_at);
      const callMonth = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
      return callMonth === monthYear;
    });
    
    if (monthCalls.length === 0) {
      return 'No Status';
    }
    
    // Get the billing_status from the first call in this month
    const firstCallStatus = monthCalls[0]?.billing_status;
    
    // Also check all calls in this month for any billing status
    const allStatuses = monthCalls.map(call => call.billing_status).filter(status => status && status !== 'null' && status !== 'undefined');
    
    if (allStatuses.length > 0) {
      // Return the first non-null status found
      return allStatuses[0];
    }
    
    // If no valid status found in calls, check if there are any calls with billing_status
    const callsWithStatus = monthCalls.filter(call => call.billing_status && call.billing_status !== 'null' && call.billing_status !== 'undefined');
    if (callsWithStatus.length > 0) {
      return callsWithStatus[0].billing_status;
    }
    
    // If we have calls but no billing status, return 'Pending' instead of 'No Status'
    if (monthCalls.length > 0) {
      return 'Pending';
    }
    
    return 'No Status';
  }, [stripeInvoices, billingStatusByMonth, callDetails]);

  // Add or update the isPaymentDone helper
  const isPaymentDone = (monthYear) => {
    return getBillingStatusForMonth(monthYear) === 'Paid';
  };

  // Helper to get PDF link for a month from Stripe invoices
  const getPDFLinkForMonth = (monthYear) => {
    // First try to get from Stripe invoices
    const stripeInvoice = stripeInvoices.find(invoice => invoice.monthYear === monthYear);
    if (stripeInvoice && stripeInvoice.hosted_invoice_url) {
      return stripeInvoice.hosted_invoice_url;
    }
    
    // Try to get from server billing status data
    const billingStatusData = billingStatusByMonth.find(item => item.month_year === monthYear);
    if (billingStatusData && billingStatusData.pdf_link) {
      return billingStatusData.pdf_link;
    }
    
    // Fallback to database
    const monthCalls = callDetails.filter(call => {
      const callDate = new Date(call.created_at);
      const callMonth = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
      return callMonth === monthYear;
    });
    
    // Get the first call with a PDF link
    const callWithPDF = monthCalls.find(call => call.pdf_link);
    return callWithPDF?.pdf_link || null;
  };

  // Debug function to log billing status data
  const debugBillingStatus = (monthYear) => {
    const monthCalls = callDetails.filter(call => {
      const callDate = new Date(call.created_at);
      const callMonth = `${callDate.getFullYear()}-${String(callDate.getMonth() + 1).padStart(2, '0')}`;
      return callMonth === monthYear;
    });
    
    console.log(`🔍 Debug billing status for ${monthYear}:`);
    console.log('📊 Total calls in month:', monthCalls.length);
    console.log('📋 Billing statuses:', monthCalls.map(call => ({
      id: call.id,
      created_at: call.created_at,
      billing_status: call.billing_status,
      pdf_link: call.pdf_link
    })));

    // Also log Stripe invoices for this month
    const stripeInvoice = stripeInvoices.find(invoice => invoice.monthYear === monthYear);
    if (stripeInvoice) {
      console.log('📄 Stripe invoice found:', {
        id: stripeInvoice.id,
        monthYear: stripeInvoice.monthYear,
        amount: stripeInvoice.amount,
        status: stripeInvoice.status,
        hosted_invoice_url: stripeInvoice.hosted_invoice_url
      });
    } else {
      console.log('📄 No Stripe invoice found for this month');
    }
    
    // Log the final status that will be displayed
    const finalStatus = getBillingStatusForMonth(monthYear);
    console.log('🎯 Final displayed status:', finalStatus);
    
    return monthCalls;
  };

  // Memoize current month summary calculations
  const currentMonthSummary = useMemo(() => {
    try {
      const summary = getCurrentMonthSummary(billingDataWithMonthly);
      return summary;
    } catch (error) {
      console.error('❌ Error in getCurrentMonthSummaryLocal:', error);
      return null;
    }
  }, [billingDataWithMonthly]);

  const currentMonthAmount = useMemo(() => {
    try {
      const amount = currentMonthSummary ? currentMonthSummary.amount : 0;
      return amount;
    } catch (error) {
      console.error('❌ Error in getCurrentMonthAmount:', error);
      return 0;
    }
  }, [currentMonthSummary]);

  const isCurrentMonthPaymentDone = useMemo(() => {
    try {
      const now = new Date();
      const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      return isPaymentDone(currentMonthYear);
    } catch (error) {
      console.error('❌ Error in isCurrentMonthPaymentDone:', error);
      return false;
    }
  }, [billingDataWithMonthly]);

  // Function to redirect to Stripe Checkout
  const redirectToStripeCheckout = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔄 Starting Stripe checkout process...');
      
      // Get the current month's billing row from the table
      console.log('📊 Current month summary:', currentMonthSummary);
      
      if (!currentMonthSummary) {
        console.log('❌ No current month summary found');
        alert('No billing data available for current month.');
        setLoading(false);
        return;
      }
      
      if (currentMonthSummary.amount <= 0) {
        console.log('❌ Current month amount is zero or negative:', currentMonthSummary.amount);
        alert('No billing amount available for current month.');
        setLoading(false);
        return;
      }

      const userEmail = getUserEmail();
      const currentMonthYear = getCurrentMonthYear();
      
      console.log('📋 Checkout session data:', {
        amount: currentMonthSummary.amount,
        monthYear: currentMonthSummary.title || currentMonthYear,
        userEmail: userEmail,
        userName: userName
      });

      // Create checkout session
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: currentMonthSummary.amount,
          monthYear: currentMonthSummary.title || currentMonthYear,
          userEmail: userEmail,
          userName: userName
        }),
      });

      console.log('📡 Backend response status:', response.status);
      console.log('📡 Backend response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', errorText);
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📄 Backend response data:', data);
      
      if (!data.sessionId) {
        console.error('❌ No sessionId in response:', data);
        throw new Error('No session ID received from server');
      }
      
      console.log('✅ Session ID received:', data.sessionId);
      
      // Redirect to Stripe Checkout
      const stripe = await loadStripe('pk_live_51RMOUfJZKrN6X9pNIE0K4ADsWn0K1t2zovlkrnmKQIcA0bHhNs141ofA1UlX2ctp0OWZvv5tSEVZG7kLKVKKYCuu00ZMM27ciP');
      
      if (!stripe) {
        console.error('❌ Failed to load Stripe');
        throw new Error('Failed to load Stripe payment system');
      }
      
      console.log('🔄 Redirecting to Stripe checkout...');
      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      
      if (error) {
        console.error('❌ Stripe redirect error:', error);
        alert(`Error opening payment page: ${error.message}`);
      } else {
        console.log('✅ Successfully redirected to Stripe checkout');
      }
    } catch (error) {
      console.error('❌ Error in redirectToStripeCheckout:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      let errorMessage = 'Error creating payment session. Please try again.';
      
      if (error.message.includes('Backend error')) {
        errorMessage = `Server error: ${error.message}`;
      } else if (error.message.includes('Failed to load Stripe')) {
        errorMessage = 'Payment system temporarily unavailable. Please try again.';
      } else if (error.message.includes('No session ID')) {
        errorMessage = 'Payment session creation failed. Please try again.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentMonthSummary, userName]);

  const handlePayNow = useCallback((monthYear, amount) => {
    console.log('🔄 Pay Now clicked for:', { monthYear, amount });
    
    if (amount > 0) {
      console.log('✅ Amount is greater than 0, redirecting to checkout...');
      redirectToStripeCheckout();
    } else {
      console.log('❌ Amount is 0 or negative, showing alert');
      alert('No payment amount available for this period.');
    }
  }, [redirectToStripeCheckout]);

  // COMMENTED OUT: Sync with Retellai functionality
  // const handleSyncBilling = useCallback(async () => {
  //   setLoading(true);
  //   setIsSyncing(true);
  //   setSyncMessage('');
  //   setSyncMessageType('');
  //   
  //   try {
  //     console.log('🔄 Starting manual billing sync...');
  //     setSyncMessage('Starting sync from Retell.ai...');
  //     setSyncMessageType('info');
  //     
  //     // PRESERVE CURRENT DATA BEFORE SYNC
  //     console.log('💾 Preserving current billing data before sync...');
  //     setPreSyncCallDetails(JSON.parse(JSON.stringify(callDetails)));
  //     setPreSyncBillingStatus(JSON.parse(JSON.stringify(billingStatusByMonth)));
  //     setPreSyncPhoneNumberBilling(JSON.parse(JSON.stringify(phoneNumberBilling)));
  //     
  //     const userEmail = getUserEmail();
  //     
  //     if (!userEmail) {
  //       throw new Error('User email not found. Please log in again.');
  //     }
  //     
  //     console.log('📧 Syncing for user:', userEmail);
  //     setSyncMessage('Connecting to Retell.ai...');
  //     
  //     const response = await fetch(`${API_BASE_URL}/api/billing/${encodeURIComponent(userEmail)}/sync`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       timeout: 30000 // 30 second timeout
  //     });
  //     
  //     console.log('📡 Response status:', response.status);
  //     
  //     if (!response.ok) {
  //       let errorMessage = `HTTP ${response.status}`;
  //       try {
  //         const errorData = await response.json();
  //         errorMessage = errorData.error || errorData.message || errorMessage;
  //       } catch (e) {
  //         // If we can't parse the error response, use the status
  //         errorMessage = `Server error: ${response.status} ${response.statusText}`;
  //       }
  //       throw new Error(errorMessage);
  //     }
  //     
  //     const result = await response.json();
  //     console.log('✅ Sync completed:', result);
  //     
  //     // Check if sync made any updates
  //     if (result.callsUpdated === 0) {
  //       console.log('🛡️ Sync reported 0 updates - applying Retell.ai totals manually');
  //       setSyncMessage('Applying Retell.ai totals manually...');
  //       
  //       // Since sync couldn't update the database, manually update the consolidated entry in memory
  //       // with the correct Retell.ai totals ($52.39)
  //       const updatedCallDetails = [...(preSyncCallDetails || callDetails)];
  //       
  //       // Find and update the October consolidated entry
  //       const octoberIndex = updatedCallDetails.findIndex(call => 
  //         call.call_id && call.call_id.toLowerCase().includes('october')
  //       );
  //       
  //       if (octoberIndex !== -1) {
  //         console.log('🔄 Manually updating October consolidated entry with Retell.ai totals');
  //         updatedCallDetails[octoberIndex] = {
  //           ...updatedCallDetails[octoberIndex],
  //           total_cost: 5239, // Convert to cents
  //           voice_engine_cost: 2493, // Convert to cents
  //           llm_cost: 1689, // Convert to cents
  //           kb_cost: 91, // Convert to cents
  //           text_testing_cost: 718, // Convert to cents
  //           // Add the missing surcharges
  //           short_call_surcharge: 13, // Convert to cents
  //           llm_token_surcharge: 35 // Convert to cents
  //         };
  //         
  //         console.log('🔄 Updated consolidated entry:', {
  //           call_id: updatedCallDetails[octoberIndex].call_id,
  //           total_cost: updatedCallDetails[octoberIndex].total_cost,
  //           voice_engine_cost: updatedCallDetails[octoberIndex].voice_engine_cost,
  //           llm_cost: updatedCallDetails[octoberIndex].llm_cost,
  //           kb_cost: updatedCallDetails[octoberIndex].kb_cost,
  //           text_testing_cost: updatedCallDetails[octoberIndex].text_testing_cost,
  //           short_call_surcharge: updatedCallDetails[octoberIndex].short_call_surcharge,
  //           llm_token_surcharge: updatedCallDetails[octoberIndex].llm_token_surcharge
  //         });
  //         
  //         // Log the full object to see what we're actually updating
  //         console.log('🔄 FULL UPDATED ENTRY:', updatedCallDetails[octoberIndex]);
  //         
  //         // Force a complete state update to trigger memo recalculation
  //         setCallDetails([...updatedCallDetails]);
  //         setBillingStatusByMonth(preSyncBillingStatus || billingStatusByMonth);
  //         setPhoneNumberBilling(preSyncPhoneNumberBilling || phoneNumberBilling);
  //         
  //         // Force memo recalculation
  //         setForceRefresh(prev => prev + 1);
  //         
  //         // Force a small delay to ensure state updates are processed
  //         setTimeout(() => {
  //           console.log('🔄 State update completed, display should now show $52.39');
  //         }, 100);
  //         
  //         setSyncMessage(`✅ Sync completed! Applied Retell.ai totals manually (${result.callsUpdated} database updates from ${result.agentsProcessed} agents). Display updated with latest totals.`);
  //         setSyncMessageType('success');
  //       } else {
  //         console.log('⚠️ Could not find October consolidated entry to update');
  //         // Restore original data if we can't find the entry
  //         setCallDetails(preSyncCallDetails || callDetails);
  //         setBillingStatusByMonth(preSyncBillingStatus || billingStatusByMonth);
  //         setPhoneNumberBilling(preSyncPhoneNumberBilling || phoneNumberBilling);
  //         
  //         setSyncMessage(`✅ Sync completed! No updates found (${result.callsUpdated} calls updated from ${result.agentsProcessed} agents). Display preserved.`);
  //         setSyncMessageType('success');
  //       }
  //     } else {
  //       console.log('🔄 Sync made updates - refreshing data from server');
  //       setSyncMessage('Refreshing data from server...');
  //       
  //       // Refresh data from server after successful sync
  //       try {
  //         const userEmail = getUserEmail();
  //         await refreshBillingData(userEmail, true);
  //         console.log('✅ Data refreshed successfully after sync');
  //         setSyncMessage(`✅ Sync completed! Updated ${result.callsUpdated} calls from ${result.agentsProcessed} agents. Data refreshed with latest totals.`);
  //         setSyncMessageType('success');
  //       } catch (refreshError) {
  //         console.error('❌ Error refreshing data after sync:', refreshError);
  //         setSyncMessage(`✅ Sync completed! Updated ${result.callsUpdated} calls from ${result.agentsProcessed} agents. Please refresh the page to see updated totals.`);
  //         setSyncMessageType('warning');
  //       }
  //     }
  //     
  //   } catch (error) {
  //     console.error('❌ Error syncing billing:', error);
  //     
  //     // Restore original data on error
  //     if (preSyncCallDetails) {
  //       console.log('🛡️ Restoring original data due to sync error');
  //       setCallDetails(preSyncCallDetails);
  //       setBillingStatusByMonth(preSyncBillingStatus);
  //       setPhoneNumberBilling(preSyncPhoneNumberBilling);
  //     }
  //     
  //     // Set error message based on error type
  //     let errorMessage = error.message;
  //     if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
  //       errorMessage = 'Network error: Unable to connect to server. Please check your internet connection and try again.';
  //     } else if (error.message.includes('500')) {
  //       errorMessage = 'Server error: The sync service is temporarily unavailable. Please try again later.';
  //     } else if (error.message.includes('401') || error.message.includes('403')) {
  //       errorMessage = 'Authentication error: Please log in again and try syncing.';
  //     } else if (error.message.includes('timeout')) {
  //       errorMessage = 'Request timeout: The sync is taking too long. Please try again.';
  //     }
  //     
  //     setSyncMessage(`❌ Sync failed: ${errorMessage}`);
  //     setSyncMessageType('error');
  //   } finally {
  //     setLoading(false);
  //     setIsSyncing(false);
  //     
  //     // Clear sync message after 8 seconds
  //     setTimeout(() => {
  //       setSyncMessage('');
  //       setSyncMessageType('');
  //     }, 8000);
  //   }
  // }, [refreshBillingData, callDetails, billingStatusByMonth, phoneNumberBilling, preSyncCallDetails, preSyncBillingStatus, preSyncPhoneNumberBilling]);

  // DISABLED: This useEffect was overriding the total amount from the new API
  // The new API already includes all components (voice, chat, phone) in the total
  // useEffect(() => {
  //   if (phoneNumberBilling.length > 0 && billingDataWithMonthly.length > 0) {
  //     if (currentMonthSummary) {
  //       setTotalAmount(currentMonthSummary.amount);
  //     }
  //   }
  // }, [phoneNumberBilling, billingDataWithMonthly, currentMonthSummary, setTotalAmount]);

  return (
    <>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(-10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <div className="container d-flex flex-column h-100" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ background: '#fff', borderRadius: '18px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '32px 32px 24px 32px', minWidth: '900px', maxWidth: '1100px', width: '100%', color: '#111', marginTop: '0', marginBottom: '48px', minHeight: 500 }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#1f2937' }}>Billing</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            {/* COMMENTED OUT: Sync with Retellai button */}
            {/* <button
              style={{
                background: isSyncing ? '#059669' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isSyncing ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: isSyncing ? 0.8 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={handleSyncBilling}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Syncing...
                </>
              ) : (
                '🔄 Sync from Retell.ai'
              )}
            </button> */}
            <button
              style={{
                background: isCurrentMonthPaymentDone ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isCurrentMonthPaymentDone ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: isCurrentMonthPaymentDone ? 0.6 : 1
            }}
            onClick={() => {
                if (!isCurrentMonthPaymentDone) {
                    redirectToStripeCheckout();
                }
            }}
              disabled={loading || isCurrentMonthPaymentDone}
          >
              {loading ? 'Opening Payment...' : 
               isCurrentMonthPaymentDone ? 'Payment Completed' : 'Manage Payment'}
          </button>
          {/* Payment Status Info */}
          {isCurrentMonthPaymentDone && (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px 12px', 
              background: '#f0f9ff', 
              border: '1px solid #0ea5e9', 
              borderRadius: '6px',
              fontSize: '12px',
              color: '#0369a1'
            }}>
              ✅ Current month payment completed. New charges will be available for payment next month.
            </div>
          )}
          </div>
        </div>
        
        {/* Sync Message Display */}
        {syncMessage && (
          <div style={{
            marginBottom: '20px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: syncMessageType === 'success' ? '#d1fae5' : 
                           syncMessageType === 'error' ? '#fee2e2' : 
                           syncMessageType === 'warning' ? '#fef3c7' : '#e0f2fe',
            color: syncMessageType === 'success' ? '#065f46' : 
                   syncMessageType === 'error' ? '#991b1b' : 
                   syncMessageType === 'warning' ? '#92400e' : '#0c4a6e',
            border: `1px solid ${syncMessageType === 'success' ? '#a7f3d0' : 
                               syncMessageType === 'error' ? '#fecaca' : 
                               syncMessageType === 'warning' ? '#fde68a' : '#bae6fd'}`,
            animation: 'fadeIn 0.3s ease-in'
          }}>
            {syncMessageType === 'success' && '✅'}
            {syncMessageType === 'error' && '❌'}
            {syncMessageType === 'warning' && '⚠️'}
            {syncMessageType === 'info' && 'ℹ️'}
            {syncMessage}
          </div>
        )}
        
        {/* Amount Breakdown Section (REMOVE THIS SECTION) */}
        {/* Billing History Content */}
        <div style={{ marginTop: 0 }}>
          {/* Table with accordion rows */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'auto', maxHeight: '500px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: 600, color: '#374151', fontSize: '16px', width: '32%' }}>Title</th>
                  <th style={{ textAlign: 'right', padding: '14px 20px', fontWeight: 600, color: '#374151', fontSize: '16px', width: '16%' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: 600, color: '#374151', fontSize: '16px', width: '32%' }}>Details</th>
                  <th style={{ textAlign: 'left', padding: '14px 20px', fontWeight: 600, color: '#374151', fontSize: '16px', width: '20%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {billingDataWithMonthly.map(item => renderAccordionTableRow(item))}
              </tbody>
            </table>
          </div>
        </div>
        

      </div>
    </div>
    </>
  );
};

// Standalone helper to get the current month summary
export function getCurrentMonthSummary(billingDataWithMonthly) {
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return billingDataWithMonthly.find(row => row.monthYear === currentMonthKey || row.title?.includes(now.toLocaleString('default', { month: 'long', year: 'numeric' })));
}

// Standalone helper to get the current month's invoice amount
export function getCurrentMonthInvoiceAmount(billingDataWithMonthly) {
  const summary = getCurrentMonthSummary(billingDataWithMonthly);
  return summary ? summary.amount : 0;
}



// Standalone helper to get the monthly billing summary
export function getMonthlyBillingSummary(calls) {
  const summary = {};
  calls.forEach(call => {
    // Extract costs using the same logic as extractCallCosts
    let costs = {
      voice_cost: 0,
      llm_cost: 0,
      kb_cost: 0,
      total_cost: 0,
      duration_seconds: 0
    };
    
    // First try to get costs from the new billing fields
    if (call.total_cost) {
      costs.total_cost = parseFloat(call.total_cost); // Backend already returns dollars
    }
    
    if (call.total_duration_seconds) {
      costs.duration_seconds = parseInt(call.total_duration_seconds);
    }
    
    // Extract detailed costs from call_cost JSON
    if (call.call_cost) {
      try {
        const callCostData = typeof call.call_cost === 'string' ? 
          JSON.parse(call.call_cost) : call.call_cost;
        
        if (callCostData.product_costs && Array.isArray(callCostData.product_costs)) {
          callCostData.product_costs.forEach(product => {
            const cost = parseFloat(product.cost || 0); // Backend already returns dollars
            switch (product.product) {
              case 'Voice Engine':
              case 'elevenlabs_tts':
                costs.voice_cost = cost;
                break;
              case 'LLM':
              case 'gpt_4_1':
              case 'gpt4o':
              case 'gpt4o_mini':
                costs.llm_cost = cost;
                break;
              case 'Knowledge Base Usage':
              case 'knowledge_base_usage':
                costs.kb_cost = cost;
                break;
              case 'Phone Number':
              case 'phone_number':
                costs.phone_cost = cost;
                break;
              case 'Text Testing':
              case 'text_testing':
                costs.text_testing_cost = cost;
                break;
              case 'Short Call Surcharge':
              case 'short_call_surcharge':
                costs.surcharge_cost = cost;
                break;
              case 'LLM Token Surcharge':
              case 'llm_token_surcharge':
                costs.llm_surcharge_cost = cost;
                break;
            }
          });
        }
      } catch (e) {
        console.error('Error parsing call_cost:', e);
      }
    }
    
    // Fallback to old call_analysis if new billing data is not available
    if (costs.total_cost === 0 && call.call_analysis) {
      try {
        const analysis = typeof call.call_analysis === 'string' ? 
          JSON.parse(call.call_analysis) : call.call_analysis;
        
        if (analysis.call_cost) {
          costs.voice_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'elevenlabs_tts')?.cost || 0;
          costs.llm_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'gpt_4_1')?.cost || 0;
          costs.kb_cost = analysis.call_cost.product_costs?.find(pc => pc.product === 'knowledge_base_usage')?.cost || 0;
          costs.total_cost = analysis.total_cost || 0;
          costs.duration_seconds = analysis.call_cost.total_duration_seconds || 0;
        }
      } catch (e) {
        console.error('Error parsing call_analysis:', e);
      }
    }
    
    if (!costs) return;
    // Group by month
    const dateObj = new Date(call.created_at);
    const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    if (!summary[monthKey]) {
      summary[monthKey] = {
        voice_cost: 0,
        llm_cost: 0,
        kb_cost: 0,
        total_cost: 0,
        total_duration: 0,
        call_count: 0
      };
    }
    summary[monthKey].voice_cost += costs.voice_cost;
    summary[monthKey].llm_cost += costs.llm_cost;
    summary[monthKey].kb_cost += costs.kb_cost;
    summary[monthKey].total_cost += costs.total_cost;
    summary[monthKey].total_duration += costs.duration_seconds;
    summary[monthKey].call_count += 1;
  });
  return summary;
}

export default Billing; 